// Imports for external libraries go here.
import { FC, useCallback, useEffect, useState } from 'react';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { Accordion } from '@marriott/mi-ui-library';
import { Heading, Text, Types, Button } from '@marriott/mi-ui-library';
import { useUserDetailsGlobalStore } from '@marriott/mi-store-utils';
import { GenericDialogueModal } from '../../molecules/GenericDialogueModal';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { usePathname, useRouter } from 'next/navigation';
import { updateDatalayer } from '../../util/dataLayer/datalayerUtil';
import moment from 'moment';

// Imports for internal (to the monorepo) libraries go here,
import { StyledSubmitContainer } from './Submit.style';
import { SubmitFormProps, amenitiesDetailsProps } from './Submit.types';
import { SurveyWrapper } from '../../molecules/SurveyWrapper';
import { brandColorSchemeGenerator } from '../../util/constants';
import { useCreateSurveyStore } from '../../store/createSurveyStore';
import { DiningPlanDetail } from './Submit.types';
import DOMPurify from 'isomorphic-dompurify';
import { capitalize, removeXSSScripts } from '../../util/helpers';
import ConsentCheckbox from '../../modules/ConsentCheckbox/ConsentCheckbox';
import { validateStayNotStarted } from '../../util/prearrivalValidation';
import { ErrorDetail } from '../Error';
import { getResConfirmationDatePrefix } from '../../util/dataLayer/resConfirmationNumberUtil';

// import { formData? } from './formData_mock';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const SubmitForm: FC<SubmitFormProps> = (props: any) => {
  const { formData, setFormData, setTotalPoint, form_prearrival_questions_complete } = useCreateSurveyStore(); // Access formData? from Zustand store

  const { isShowError: stayValidationError, errorMsg: stayErrorMsg } = validateStayNotStarted({
    arrivalDate: formData?.reservation?.arrival,
    isAuthorMode: props?.isAuthorMode ?? false,
    confirmationNumber: formData?.confirmationNumber || null,
    marshaCode: formData?.marshaCode || null,
  });

  const [isLoading, setIsLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [modalType, setModalType] = useState<'exitModal' | 'submitModal' | 'problemModal' | 'alreadySubmittedModal'>(
    'submitModal'
  );
  const [errorHeading, setErrorHeading] = useState('');
  const [errordesc, setErrordesc] = useState('');
  const marshaCode = formData?.property_config?.marsha_cd;
  const amenitiesData = formData?.property_config?.amenities ?? props.property_config?.amenities;
  const headingText = amenitiesData?.some((item: amenitiesDetailsProps) => item.category === 'dining_page')
    ? 'Other'
    : '';
  const headingExperienceText = amenitiesData?.some(
    (item: amenitiesDetailsProps) => item.category === 'activities_page'
  )
    ? 'Other'
    : '';
  dayjs.extend(advancedFormat);
  // Data from reservation
  const [propertyName, setPropertyName] = useState('');
  const [propertyAddress, setPropertyAddress] = useState(null);
  const [firstLastName, setFirstLastName] = useState('');
  const [reservation_num, setReservation_num] = useState(null);
  const [arrivalDate, setArrivalDate] = useState(moment().format('MMM D, YYYY'));
  const [departureDate, setDepartureDate] = useState(moment().format('MMM D, YYYY'));
  const [alternateCID, setAlternateCID] = useState('');
  const [confirmationNumber, setConfirmationNumber] = useState('');
  const [currentDate, setCurrentDate] = useState(moment().format('MMDDYYYY'));
  const [arrivalMethod, setArrivalMethod] = useState('');
  const [arrivalPropertyTime, setArrivalPropertyTime] = useState('');
  const [arrivalTiming, setArrivalTiming] = useState('');
  const [departureMethod, setDepartureMethod] = useState('');
  const [departurePropertyTime, setDeparturePropertyTime] = useState('');
  const [departureTiming, setDepartureTiming] = useState('');
  const [arrivalDeptNotes, setArrivalDeptNotes] = useState('');
  const [arrivalAnticipateTime, setArrivalAnticipateTime] = useState('');
  const [accessibilityNeeds, setAccessibilityNeeds] = useState('');
  const [accessibilityNotes, setAccessibilityNotes] = useState('');
  const [tripEmailCheckbox, setTripEmailCheckbox] = useState(true);
  const [tripPhoneCheckbox, setTripPhoneCheckbox] = useState(true);
  const [tripTextCheckbox, setTripTextCheckbox] = useState(true);
  const [tripWechatCheckbox, setTripWechatCheckbox] = useState(true);
  const [tripWhatsappCheckbox, setTripWhatsappCheckbox] = useState(true);
  const [customerEmail, setCustomerEmail] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [preferredContactMethodDetails, setPreferredContactMethodDetails] = useState<any>([]);
  const [specialOccasionRequestCategoryDetails, setSpecialOccasionRequestCategoryDetails] = useState('');
  const [specialOccasionRequestOtherNotes, setSpecialOccasionRequestOtherNotes] = useState('');
  const [specialOccasionRequestNotes, setSpecialOccasionRequestNotes] = useState('');
  const [surpriseRequest, setSurpriseRequest] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [surpriseRequestCoordinationContactDetail, setSurpriseRequestCoordinationContactDetail] = useState<any>([]);
  const [additionalRequest, setAdditionalRequest] = useState('');
  const [enableConsentCheckboxFeature, setEnableConsentCheckboxFeature] = useState<boolean>(false);
  const { signInTargetDisabledFlagAlternate = false } = props?.model ?? {};

  useEffect(() => {
    if (!signInTargetDisabledFlagAlternate) {
      setEnableConsentCheckboxFeature(true);
    }
  }, [signInTargetDisabledFlagAlternate]);

  const combinedValue = `${currentDate}${confirmationNumber}`;
  /* eslint-disable  @typescript-eslint/no-explicit-any */
  const i18nText: any = props?.i18nMap;
  useEffect(() => {
    if (
      props?.isAuthorMode ||
      formData?.property_config?.brand_cd ||
      props?.submitURL ||
      props?.reservation ||
      formData?.property_config?.amenities
    ) {
      setIsLoading(false);
    }
  }, [
    props?.isAuthorMode,
    formData?.property_config?.brand_cd,
    props?.submitURL,
    props?.reservation,
    formData?.property_config?.amenities,
  ]);
  useEffect(() => {
    setTotalPoint();
    setAdditionalRequest(formData?.additional_requests);
    setSurpriseRequestCoordinationContactDetail(formData?.surprise_request_coordination_contact_detail);
    setSurpriseRequest(formData?.surprise_request);
    setSpecialOccasionRequestNotes(formData?.special_occasion_request_notes);
    setSpecialOccasionRequestOtherNotes(formData?.special_occasion_request_other_notes);
    setSpecialOccasionRequestCategoryDetails(formData?.special_occasion_request_category_details);
    setPreferredContactMethodDetails(formData?.preferred_contact_method_details);
    setCustomerEmail(formData?.reservation?.cusotmerEmail);
    setPropertyName(formData?.property_config?.hotel_name);
    setPropertyAddress(formData?.property_config?.hotel_location);
    setFirstLastName(
      capitalize(formData?.reservation?.customerFirstName) + ' ' + capitalize(formData?.reservation?.customerLastName)
    );
    setReservation_num(formData?.reservation?.confirmationNumber);
    setArrivalDate(dayjs(formData?.reservation?.arrival).format('MMM D, YYYY'));
    setDepartureDate(dayjs(formData?.reservation?.departure).format('MMM D, YYYY'));
    setArrivalMethod(formData?.arrival_method);
    setArrivalPropertyTime(formData?.transportation_arrival_date);
    setArrivalTiming(formData?.transportation_arrival_time);
    setDepartureMethod(formData?.departure_method);
    setDeparturePropertyTime(formData?.transportation_departure_date);
    setDepartureTiming(formData?.transportation_departure_time);
    setArrivalDeptNotes(formData?.arrival_dept_notes);
    setArrivalAnticipateTime(formData?.arrival_anticipate_time);
    setAccessibilityNeeds(formData?.accessiblity_needs);
    setAccessibilityNotes(formData?.accessibility_notes);
    setAlternateCID(formData?.reservation?.AlternateCustomerId);
    setConfirmationNumber(formData?.reservation?.confirmationNumber || props?.reservation?.confirmationNumber || '');
    setCurrentDate(getResConfirmationDatePrefix(formData?.reservation?.confoCreate || props?.reservation?.confoCreate));
    setTripEmailCheckbox(formData?.tripEmailCheckbox);
    setTripPhoneCheckbox(formData?.tripPhoneCheckbox);
    setTripTextCheckbox(formData?.tripTextCheckbox);
    setTripWechatCheckbox(formData?.tripWechatCheckbox);
    setTripWhatsappCheckbox(formData?.tripWhatsappCheckbox);
  }, [formData, props?.reservation?.confoCreate]);
  //Data from AEM
  const title = props.model?.title; // Your Travel Plan
  const description = props.model?.description;
  const diningAccordionLabel = props.model?.diningAccordionLabel;
  const guestDetailsAccordionLabel = props.model?.guestDetailsAccordionLabel;
  const experienceAccordionLabel = props.model?.activitiesAccordionLabel;
  const privacyCenterDescription = props.model?.privacyCenterDescription;
  const consentCheckbox = props?.model?.consentCheckbox;

  const labels = {
    address: propertyName + ',' + propertyAddress,
    subTitle2: firstLastName,

    subTitle3: `<b> ${props?.i18nMap?.arrival ?? 'Arrival'}: </b>${arrivalDate}`,
    subTitle4: `<b> ${props?.i18nMap?.reservationNumber ?? 'Reservation #'}: </b>${reservation_num}`,
    subTitle5: `<b> ${props?.i18nMap?.departure ?? 'Departure'}: </b>${departureDate}`,
  };

  // Edit button
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Edit = (props: any) => {
    return (
      <div className="edit__text">
        <Button
          // className="m-button-m m-button-primary custom-btn mx-0 "
          // type={Types.ButtonTypeVariation.Button}
          buttonCopy="EDIT"
          callback={() => handleNextPage(props.page + '.mi')}
        ></Button>
      </div>
    );
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const findContactDetail = (contacts: any, type: string) => {
    const contact = contacts?.find(
      (contact: { contact_method_type: string }) => contact.contact_method_type.toLowerCase() === type.toLowerCase()
    );
    return contact ? contact.contact_method_detail : null;
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const findContactTimezone = (contacts: any, type: string) => {
    const contact = contacts?.find(
      (contact: { contact_method_type: string }) => contact.contact_method_type.toLowerCase() === type.toLowerCase()
    );
    return contact ? contact.timezone_of_guest : null;
  };
  const soTripCustomerEmail = findContactDetail(surpriseRequestCoordinationContactDetail, 'email') || '';
  const sotripPhoneNumber = findContactDetail(surpriseRequestCoordinationContactDetail, 'phone') || '';
  const sotripTextNumber = findContactDetail(surpriseRequestCoordinationContactDetail, 'text') || '';
  const sotripWeChatNumber = findContactDetail(surpriseRequestCoordinationContactDetail, 'wechat') || '';
  const sotripWhatsAppNumber = findContactDetail(surpriseRequestCoordinationContactDetail, 'whatsapp') || '';
  const sotripPhoneNumberTimezone = findContactTimezone(surpriseRequestCoordinationContactDetail, 'phone') || 'Select';
  const sotripTextNumberTimezone = findContactTimezone(surpriseRequestCoordinationContactDetail, 'text') || 'Select';

  // Grouping the dining data
  const groupDetailsByDate = (diningDetails: DiningPlanDetail[]) => {
    return diningDetails.reduce((acc, detail) => {
      // Use both preferred date and alternate date
      const preferredDate = moment(detail.prefered_date);
      const alternateDate = moment(detail.alternate_date);

      if (preferredDate && preferredDate.isValid()) {
        const formattedDate = moment(preferredDate).format('dddd YYYY-MM-DD');
        if (!acc[formattedDate]) {
          acc[formattedDate] = [];
        }
        acc[formattedDate].push({ ...detail, type: 'Preferred' });
      }

      if (alternateDate && alternateDate.isValid()) {
        const formattedDate = moment(alternateDate).format('dddd YYYY-MM-DD');

        if (!acc[formattedDate]) {
          acc[formattedDate] = [];
        }
        acc[formattedDate].push({ ...detail, type: 'Alternative' });
      }
      return acc;
    }, {} as { [key: string]: (DiningPlanDetail & { type: string })[] });
  };
  const otherReservationReqDetails =
    formData?.dining_plan_details?.filter((detail: DiningPlanDetail) => detail.reservation_req !== '0') || [];
  const otherActReservationReqDetails =
    formData?.experiences_plan_details?.filter((detail: DiningPlanDetail) => detail.reservation_req !== '0') || [];

  const groupedDiningDetails = groupDetailsByDate(otherReservationReqDetails);
  const groupedActivitiesDetails = groupDetailsByDate(otherActReservationReqDetails);
  const hasData = Object.keys(groupedDiningDetails).length > 0;

  const hasActivityData = Object.keys(groupedActivitiesDetails).length > 0;
  const hasDiningData =
    formData?.dining_plan_details?.filter((detail: DiningPlanDetail) => detail.reservation_req === '0') || [];

  const hasActivitysData =
    formData?.experiences_plan_details?.filter((detail: DiningPlanDetail) => detail.reservation_req === '0') || [];
  // Check if there's any data
  const hasDining = hasDiningData.length > 0;
  const hasActivity = hasActivitysData.length > 0;
  const pathName = usePathname();
  const router = useRouter();
  const handleNextPage = (newPart?: string) => {
    const currentPath = pathName;
    const newPath = currentPath.replace(/\/[^/]*$/, `/${newPart}`);
    // handleRedirect(newPath);
    router.push(newPath);
  };
  const handleClose = () => {
    setOpenModal(false);
  };

  const handleSubmitButton = () => {
    if (modalType === 'problemModal' || modalType === 'exitModal' || modalType === 'alreadySubmittedModal') {
      handleClose();
    } else {
      handleSubmit();
    }
  };

  const isSignedInUser = useUserDetailsGlobalStore(state => state?.isSignedInUser);

  const checkConsentStatus = useCallback(
    (userAlreadyConsented: boolean, userSignedIn: boolean, consentStateVariable: boolean) => {
      // consentStateVariable = false && userSignedIn = false;  ----> 'unchecked'
      // userAlreadyConsented = true && userSignedIn = true; --------> 'not_eligible'
      // userAlreadyConsented = false && userSignedIn = true; -------> 'checked'
      if (userSignedIn) {
        if (userAlreadyConsented) {
          return 'not_eligible';
        } else {
          if (consentStateVariable) {
            return 'checked';
          } else {
            return 'unchecked';
          }
        }
      } else {
        if (consentStateVariable) {
          return 'checked';
        } else {
          return 'unchecked';
        }
      }
    },
    []
  );

  const handleSubmit = async () => {
    const arrivalCheck = dayjs().isBefore(dayjs(formData?.reservation?.arrival));
    if (!arrivalCheck && !props.isAuthorMode) {
      setModalType('problemModal');
      setErrorHeading('Stay Already Started');
      setErrordesc('This form can no longer be submitted because your stay has already started.');
      setOpenModal(true);
      return;
    }

    try {
      // Construct confirmation_html

      const confirmation_html = `<table class="table">
  <table role"presentation" width="100%" border="0" cellspacing="0" cellpadding="0" bgcolor="#ffffff">
     <tbody>
        <tr>
           <td style="padding: 0px 40px 0px 40px" class="pad20rl">
              <table role="presentation" width="100%" border="0" cellspacing="0" cellpadding="0">
                 <tr>
                    <td style="padding: 15px 0px 15px 0px; border-bottom: 1px solid #cccccc;" align="left"
                       valign="top">
                       <table role="presentation" width="100%" border="0" cellspacing="0" cellpadding="0">
                          <tbody>
                             <tr>
                                <th class="colsplit pad10b" style="font-weight: normal; padding-right:
                                   15px" align="left" valign="top" width="40%">
                                   <h4 style="font-size: 16px; mso-line-height-rule: exactly; line-height:
                                      120%; margin: 0px; font-weight: 400; letter-spacing: 0px;
                                      text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular' ,
                                      Arial, Helvetica, sans-serif" class="Swiss-Regular mobile_h4">
                                      <strong>Reservation</strong>:
                                   </h4>
                                </th>
                                <th class="colsplit" style="font-weight: normal" align="left" valign="top"
                                   width="60%">
                                   <span style="font-size: 14px; mso-line-height-rule: exactly; line-height:
                                      140%; margin: 0px; font-weight: 400; letter-spacing: 0px;
                                      text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular' ,
                                      Arial, Helvetica, sans-serif" class="Swiss-Regular">
                                      <strong class="${
                                        formData?.reservation_num == '' ? 'empty-field' : ''
                                      }">Reservation #: </strong class="${
        formData?.reservation?.arrival == '' ? 'empty-field' : ''
      }">${formData?.reservation_num}<br><strong>Check-In:
                                      </strong >${formData?.reservation.arrival}<br><strong class="${
        formData?.reservation?.departure == '' ? 'empty-field' : ''
      }">Check-Out: </strong>${formData?.reservation.departure}
                                    </span>
                                </th>
                             </tr>
                          </tbody>
                       </table>
                    </td>
                 </tr>
                 <tr>
                    <td style="padding: 15px 0px 15px 0px; border-bottom: 1px solid #cccccc;" align="left"
                       valign="top">
                       <table role="presentation" width="100%" border="0" cellspacing="0" cellpadding="0">
                          <tbody>
                             <tr>
                                <th class="colsplit pad10b" style="font-weight: normal; padding-right:
                                   15px" align="left" valign="top" width="40%">
                                   <h4 style="font-size: 16px; mso-line-height-rule: exactly; line-height:
                                      120%; margin: 0px; font-weight: 400; letter-spacing: 0px;
                                      text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular' ,
                                      Arial, Helvetica, sans-serif" class="Swiss-Regular mobile_h4">
                                      <strong>Guest Information</strong>:
                                   </h4>
                                </th>
                                <th class="colsplit" style="font-weight: normal" align="left" valign="top"
                                   width="60%">
                                   <span style="font-size: 14px; mso-line-height-rule: exactly; line-height:
                                      140%; margin: 0px; font-weight: 400; letter-spacing: 0px;
                                      text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular' ,
                                      Arial, Helvetica, sans-serif" class="Swiss-Regular"> ${
                                        formData?.reservation.customerFirstName
                                      } ${formData?.reservation.customerLastName}
                                    </span>
                                </th>
                             </tr>
                          </tbody>
                       </table>
                    </td>
                 </tr>
                 <tr class="${
                   formData?.additional_guests_details.length === 0 &&
                   formData?.children_details.length === 0 &&
                   formData?.pet_details.length === 0
                     ? 'empty-field'
                     : ''
                 }">
                    <td style="padding: 15px 0px 15px 0px; border-bottom: 1px solid #cccccc;" align="left"
                       valign="top">
                       <table role="presentation" width="100%" border="0" cellspacing="0" cellpadding="0">
                          <tbody>
                             <tr>
                                <th class="colsplit pad10b" style="font-weight: normal; padding-right:
                                   15px" align="left" valign="top" width="40%">
                                   <h4 style="font-size: 16px; mso-line-height-rule: exactly; line-height:
                                      120%; margin: 0px; font-weight: 400; letter-spacing: 0px;
                                      text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular' ,
                                      Arial, Helvetica, sans-serif" class="Swiss-Regular mobile_h4">
                                      <strong>Travel Companion Special Information</strong>:
                                   </h4>
                                </th>
                                <th class="colsplit" style="font-weight: normal" align="left" valign="top"
                                   width="60%">
                                  <span style="font-size: 14px; mso-line-height-rule: exactly; line-height:
                                      140%; margin: 0px; font-weight: 400; letter-spacing: 0px;
                                      text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular' ,
                                      Arial, Helvetica, sans-serif" class="Swiss-Regular">
                                    <strong class="${
                                      formData?.additional_guests_details.length === 0 ? 'empty-field' : ''
                                    }">Guest: </strong>
                                    <ol style="font-size: 14px; mso-line-height-rule: exactly; line-height: 140%; margin: 0px; font-weight: 400; letter-spacing: 0px; text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular', Arial, Helvetica, sans-serif">
                                      ${formData?.additional_guests_details
                                        .map(
                                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                          (guest: any) => `
                               <li>
                                              ${
                                                guest.title || guest.guest_first_name || guest.guest_last_name
                                                  ? `${guest.title || ''}${
                                                      guest.title && (guest.guest_first_name || guest.guest_last_name)
                                                        ? ' '
                                                        : ''
                                                    }${guest.guest_first_name || ''}${
                                                      guest.guest_first_name && guest.guest_last_name ? ' ' : ''
                                                    }${guest.guest_last_name || ''}`
                                                  : ''
                                              }${
                                            guest.relationship_type
                                              ? `${
                                                  guest.title || guest.guest_first_name || guest.guest_last_name
                                                    ? ', '
                                                    : ''
                                                }${guest.relationship_type}`
                                              : ''
                                          }
                                            </li>

                                    `
                                        )
                                        .join('')}
                                    </ol>

                                    <div class="${
                                      formData?.children_details.length === 0 ? 'empty-field' : ''
                                    }" style="font-weight: bold;">Child: </div>
                                    <ol style="font-size: 14px; mso-line-height-rule: exactly; line-height: 140%; margin: 0px; font-weight: 400; letter-spacing: 0px; text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular', Arial, Helvetica, sans-serif">
                                        ${formData?.children_details
                                          .map(
                                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                            (child: any) => `
                                       <li>
                                                ${
                                                  child.childFirstName || child.childLastName
                                                    ? `${child.childFirstName || ''}${
                                                        child.childFirstName && child.childLastName ? ' ' : ''
                                                      }${child.childLastName || ''}`
                                                    : ''
                                                }${
                                              child.childAge
                                                ? `${child.childFirstName || child.childLastName ? ', ' : ''}${
                                                    child.childAge === '<1' ||
                                                    child.childAge == '1' ||
                                                    child.childAge < 2
                                                      ? `${child.childAge} Year`
                                                      : `${child.childAge} Years`
                                                  }`
                                                : ''
                                            }
                                                ${
                                                  (child.childFirstName || child.childLastName || child.childAge) &&
                                                  child.childNotesArea
                                                    ? `<br/><b>Notes: </b>${child.childNotesArea}`
                                                    : ''
                                                }
                                                ${
                                                  !(child.childFirstName || child.childLastName || child.childAge) &&
                                                  child.childNotesArea
                                                    ? `<b>Notes: </b>${child.childNotesArea}`
                                                    : ''
                                                }
                                              </li>


                                        `
                                          )
                                          .join('')}
                                    </ol>
                                    <strong class="${
                                      formData?.pet_details.length === 0 ? 'empty-field' : ''
                                    }">Pet: </strong>
                                    ${
                                      formData?.pet_details && formData?.pet_details.length > 0
                                        ? `
                                      <ol style="font-size: 14px; mso-line-height-rule: exactly; line-height: 140%; margin: 0px; font-weight: 400; letter-spacing: 0px; text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular', Arial, Helvetica, sans-serif">
                                        ${formData?.pet_details
                                          .map(
                                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                            (pet: any) => `
                                              <li>
                                            ${
                                              pet.petName || pet.petBreed
                                                ? `${pet.petName || ''}${pet.petName && pet.petBreed ? ', ' : ''}${
                                                    pet.petBreed || ''
                                                  }${pet.petNotesArea ? `<br/><b>Notes: </b>${pet.petNotesArea}` : ''}`
                                                : `${pet.petNotesArea ? `<b>Notes: </b>${pet.petNotesArea}` : ''}`
                                            }

                                              </li>
                                            `
                                          )
                                          .join('')}
                                      </ol>
                                    `
                                        : ''
                                    }
                                    </span>
                                </th>
                             </tr>
                          </tbody>
                       </table>
                    </td>
                 </tr>
                  <tr class="${
                    tripEmailCheckbox ||
                    tripPhoneCheckbox ||
                    tripTextCheckbox ||
                    tripWechatCheckbox ||
                    tripWhatsappCheckbox
                      ? ''
                      : 'empty-field'
                  }">
                    <td style="padding: 15px 0px 15px 0px; border-bottom: 1px solid #cccccc;" align="left" valign="top">
                      <table role="presentation" width="100%" border="0" cellspacing="0" cellpadding="0">
                        <tbody>
                          <tr>
                            <th
                              class="colsplit pad10b"
                              style="font-weight: normal; padding-right: 15px"
                              align="left"
                              valign="top"
                              width="40%"
                            >
                              <h4
                                style="font-size: 16px; mso-line-height-rule: exactly; line-height: 120%; margin: 0px;
                                      font-weight: 400; letter-spacing: 0px; text-transform: none; color: #1c1c1c;
                                      font-family: 'Swiss-Regular', Arial, Helvetica, sans-serif"
                                class="Swiss-Regular mobile_h4"
                              >
                                <strong>Contact Preferences</strong>:
                              </h4>
                            </th>
                            <th
                              class="colsplit"
                              style="font-weight: normal"
                              align="left"
                              valign="top"
                              width="60%"
                            >
                              ${[
                                { type: 'email', label: 'Email', checkbox: tripEmailCheckbox },
                                { type: 'phone', label: 'Phone Call', checkbox: tripPhoneCheckbox },
                                { type: 'text', label: 'Text', checkbox: tripTextCheckbox },
                                { type: 'wechat', label: 'WeChat', checkbox: tripWechatCheckbox },
                                { type: 'whatsapp', label: 'WhatsApp', checkbox: tripWhatsappCheckbox },
                              ]
                                .filter(({ checkbox }) => checkbox)
                                .map(({ type, label }) => {
                                  const detail =
                                    preferredContactMethodDetails.find((item: any) => item.contact_method_type === type)
                                      ?.contact_method_detail ||
                                    (type === 'email' && customerEmail) ||
                                    '';

                                  const timezone =
                                    preferredContactMethodDetails.find((item: any) => item.contact_method_type === type)
                                      ?.timezone_of_guest || '';

                                  if (!detail) return '';

                                  const timeZoneDisplay = type !== 'email' && timezone ? ` ${timezone}` : '';

                                  return `
                                    <span
                                      style="font-size: 14px; mso-line-height-rule: exactly; line-height: 140%; margin: 0px;
                                            font-weight: 400; letter-spacing: 0px; text-transform: none; color: #1c1c1c;
                                            font-family: 'Swiss-Regular', Arial, Helvetica, sans-serif;"
                                      class="Swiss-Regular"
                                    >
                                      <strong>${label}:</strong> ${detail}${timeZoneDisplay}
                                    </span>`;
                                })
                                .join('<br/>')}
                            </th>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>

                 <tr class="${
                   formData?.accessiblity_details.length === 0 && formData?.accessibility_notes == ''
                     ? 'empty-field'
                     : ''
                 }">
                    <td style="padding: 15px 0px 15px 0px; border-bottom: 1px solid #cccccc;" align="left"
                       valign="top">
                       <table role="presentation" width="100%" border="0" cellspacing="0" cellpadding="0">
                          <tbody>
                             <tr>
                                <th class="colsplit pad10b" style="font-weight: normal; padding-right:
                                   15px" align="left" valign="top" width="40%">
                                   <h4 style="font-size: 16px; mso-line-height-rule: exactly; line-height:
                                      120%; margin: 0px; font-weight: 400; letter-spacing: 0px;
                                      text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular' ,
                                      Arial, Helvetica, sans-serif" class="Swiss-Regular mobile_h4">
                                      <strong>Accessibility / Disability Needs</strong>:
                                   </h4>
                                </th>
                                <th class="colsplit" style="font-weight: normal" align="left" valign="top"
                                   width="60%">
                                   ${formData?.accessiblity_details
                                     .map(
                                       // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                       (accessibility: any) => `
                                   <span style="font-size: 14px; mso-line-height-rule: exactly; line-height:
                                      140%; margin: 0px; font-weight: 400; letter-spacing: 0px;
                                      text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular' ,
                                      Arial, Helvetica, sans-serif" class="Swiss-Regular">
                                      ${accessibility}<br>
                                      `
                                     )
                                     .join('')}
                                      <strong class="${
                                        formData?.accessibility_notes == '' ? 'empty-field' : ''
                                      }">Accessibility Note: </strong>${formData?.accessibility_notes}
                                      </span>
                                </th>
                             </tr>
                          </tbody>
                       </table>
                    </td>
                 </tr>
                <tr class="${formData?.arrival_anticipate_time == '' ? 'empty-field' : ''}">
                  <td style="padding: 15px 0px 15px 0px; border-bottom: 1px solid #cccccc;" align="left" valign="top">
                    <table role="presentation" width="100%" border="0" cellspacing="0" cellpadding="0">
                      <tbody>
                        <tr>
                          <th class="colsplit pad10b" style="font-weight: normal; padding-right: 15px" align="left" valign="top" width="40%">
                            <h4 style="font-size: 16px; mso-line-height-rule: exactly; line-height: 120%; margin: 0px; font-weight: 400; letter-spacing: 0px; text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular', Arial, Helvetica, sans-serif" class="Swiss-Regular mobile_h4">
                              <strong>Arrival Anticipate Time</strong>:
                            </h4>
                          </th>
                          <th class="colsplit" style="font-weight: normal" align="left" valign="top" width="60%">
                            <span style="font-size: 14px; mso-line-height-rule: exactly; line-height: 140%; margin: 0px; font-weight: 400; letter-spacing: 0px; text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular', Arial, Helvetica, sans-serif" class="Swiss-Regular">
                              ${formData?.arrival_anticipate_time}
                            </span>
                          </th>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
                <tr class="${
                  formData?.arrival_method == '' &&
                  formData?.transportation_arrival_date == '' &&
                  formData?.transportation_arrival_time == '' &&
                  formData?.arrivalInfo == '' &&
                  formData?.arrivalFlightNumber == '' &&
                  formData?.arrivalPassengers == ''
                    ? 'empty-field'
                    : ''
                }">
                  <td style="padding: 15px 0px 15px 0px; border-bottom: 1px solid #cccccc;" align="left" valign="top">
                    <table role="presentation" width="100%" border="0" cellspacing="0" cellpadding="0">
                      <tbody>
                        <tr>
                          <th class="colsplit pad10b" style="font-weight: normal; padding-right: 15px" align="left" valign="top" width="40%">
                            <h4 style="font-size: 16px; mso-line-height-rule: exactly; line-height: 120%; margin: 0px; font-weight: 400; letter-spacing: 0px; text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular', Arial, Helvetica, sans-serif" class="Swiss-Regular mobile_h4">
                              <strong>Arrival Information</strong>:
                            </h4>
                          </th>
                          <th class="colsplit" style="font-weight: normal" align="left" valign="top" width="60%">
                            <span style="font-size: 14px; mso-line-height-rule: exactly; line-height: 140%; margin: 0px; font-weight: 400; letter-spacing: 0px; text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular', Arial, Helvetica, sans-serif" class="Swiss-Regular">
                                <strong class="${
                                  formData?.arrival_method == '' ? 'empty-field' : ''
                                }">Arrival Method: </strong>${formData?.arrival_method}<br>
                              <strong class="${
                                formData?.transportation_arrival_date == '' ? 'empty-field' : ''
                              }">Arrival Date: </strong>${formData?.transportation_arrival_date}<br>
                              <strong class="${
                                formData?.transportation_arrival_time == '' ? 'empty-field' : ''
                              }">Arrival Timing: </strong>${formData?.transportation_arrival_time}<br>
                          <strong class="${formData?.arrivalInfo == '' ? 'empty-field' : ''}">
                             Arrival Location:
                              </strong><span class="${formData?.arrivalInfo == '' ? 'empty-field' : ''}">${
        formData?.arrivalInfo || 'None'
      }<br></span>
                              <strong class="${formData?.arrivalFlightNumber == '' ? 'empty-field' : ''}">
                              Arrival Location Details: 
                              </strong><span class="${formData?.arrivalFlightNumber == '' ? 'empty-field' : ''}">${
        formData?.arrivalFlightNumber || 'None'
      }<br></span>
                              <strong class="${
                                formData?.arrivalPassengers == '' ? 'empty-field' : ''
                              }">Number of Passengers on Arrival: </strong><span class="${
        formData?.arrivalPassengers == '' ? 'empty-field' : ''
      }">${formData?.arrivalPassengers || 'None'}<br></span>
                                          </span>
                          </th>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
                <tr class="${
                  formData?.transportation_departure_date == '' &&
                  formData?.transportation_departure_time == '' &&
                  formData?.departureInfo == '' &&
                  formData?.departureFlightNumber == '' &&
                  formData?.departurePassengers == ''
                    ? 'empty-field'
                    : ''
                }">
                  <td style="padding: 15px 0px 15px 0px; border-bottom: 1px solid #cccccc;" align="left" valign="top">
                    <table role="presentation" width="100%" border="0" cellspacing="0" cellpadding="0">
                      <tbody>
                        <tr>
                          <th class="colsplit pad10b" style="font-weight: normal; padding-right: 15px" align="left" valign="top" width="40%">
                            <h4 style="font-size: 16px; mso-line-height-rule: exactly; line-height: 120%; margin: 0px; font-weight: 400; letter-spacing: 0px; text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular', Arial, Helvetica, sans-serif" class="Swiss-Regular mobile_h4">
                              <strong>Departure Information</strong>:
                            </h4>
                          </th>
                          <th class="colsplit" style="font-weight: normal" align="left" valign="top" width="60%">
                            <span style="font-size: 14px; mso-line-height-rule: exactly; line-height: 140%; margin: 0px; font-weight: 400; letter-spacing: 0px; text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular', Arial, Helvetica, sans-serif" class="Swiss-Regular">
                              <strong class="${
                                formData?.departure_method == '' ? 'empty-field' : ''
                              }">Departure Method: </strong>${formData?.departure_method}<br>
                              <strong class="${
                                formData?.transportation_departure_date == '' ? 'empty-field' : ''
                              }">Departure Date: </strong>${formData?.transportation_departure_date}<br>
                              <strong class="${
                                formData?.transportation_departure_time == '' ? 'empty-field' : ''
                              }">Departure Timing: </strong>${formData?.transportation_departure_time}<br>
                                <strong class="${formData?.departureInfo == '' ? 'empty-field' : ''}">
                                  Departure Location:
                                </strong><span class="${formData?.departureInfo == '' ? 'empty-field' : ''}">${
        formData?.departureInfo || 'None'
      }<br></span>
                                  <strong class="${formData?.departureFlightNumber == '' ? 'empty-field' : ''}">
                                  Departure Location Details: 
                                </strong><span class="${formData?.departureFlightNumber == '' ? 'empty-field' : ''}">${
        formData?.departureFlightNumber || 'None'
      }<br></span>
                              <strong class="${
                                formData?.departurePassengers == '' ? 'empty-field' : ''
                              }">Number of Passengers on Departure: </strong><span class="${
        formData?.departurePassengers == '' ? 'empty-field' : ''
      }">${formData?.departurePassengers || 'None'}<br></span>
                                          </span>
                          </th>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
                
                <tr class="${formData?.arrival_dept_notes == '' ? 'empty-field' : ''}">
                  <td style="padding: 15px 0px 15px 0px; border-bottom: 1px solid #cccccc;" align="left" valign="top">
                    <table role="presentation" width="100%" border="0" cellspacing="0" cellpadding="0">
                      <tbody>
                        <tr>
                          <th class="colsplit pad10b" style="font-weight: normal; padding-right: 15px" align="left" valign="top" width="40%">
                            <h4 style="font-size: 16px; mso-line-height-rule: exactly; line-height: 120%; margin: 0px; font-weight: 400; letter-spacing: 0px; text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular', Arial, Helvetica, sans-serif" class="Swiss-Regular mobile_h4">
                              <strong>Assist Notes</strong>:
                            </h4>
                          </th>
                          <th class="colsplit" style="font-weight: normal" align="left" valign="top" width="60%">
                            <span style="font-size: 14px; mso-line-height-rule: exactly; line-height: 140%; margin: 0px; font-weight: 400; letter-spacing: 0px; text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular', Arial, Helvetica, sans-serif" class="Swiss-Regular">
                              ${formData?.arrival_dept_notes}
                            </span>
                          </th>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
                <tr class="${formData?.additional_requests == '' ? 'empty-field' : ''}">
                  <td style="padding: 15px 0px 15px 0px; border-bottom: 1px solid #cccccc;" align="left" valign="top">

                    <table role="presentation" width="100%" border="0" cellspacing="0" cellpadding="0">
                      <tbody>
                        <tr>
                          <th class="colsplit pad10b" style="font-weight: normal; padding-right: 15px" align="left" valign="top" width="40%">
                            <h4 style="font-size: 16px; mso-line-height-rule: exactly; line-height: 120%; margin: 0px; font-weight: 400; letter-spacing: 0px; text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular', Arial, Helvetica, sans-serif" class="Swiss-Regular mobile_h4">
                              <strong>Additional Travel Details</strong>:
                            </h4>
                          </th>
                          <th class="colsplit" style="font-weight: normal" align="left" valign="top" width="60%">
                            <span style="font-size: 14px; mso-line-height-rule: exactly; line-height: 140%; margin: 0px; font-weight: 400; letter-spacing: 0px; text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular', Arial, Helvetica, sans-serif" class="Swiss-Regular">
                              ${formData?.additional_requests}
                            </span>
                          </th>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
                                <tr class="${
                                  formData?.special_occasion_request_category_details.length === 0 ? 'empty-field' : ''
                                }">
                    <td style="padding: 15px 0px 15px 0px; border-bottom: 1px solid #cccccc;" align="left"
                       valign="top">
                       <table role="presentation" width="100%" border="0" cellspacing="0" cellpadding="0">
                          <tbody>
                             <tr>
                                <th class="colsplit pad10b" style="font-weight: normal; padding-right:
                                   15px" align="left" valign="top" width="40%">
                                   <h4 style="font-size: 16px; mso-line-height-rule: exactly; line-height:
                                      120%; margin: 0px; font-weight: 400; letter-spacing: 0px;
                                      text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular' ,
                                      Arial, Helvetica, sans-serif" class="Swiss-Regular mobile_h4">
                                     <strong>Special Occasion</strong>:
                                   </h4>
                                </th>
                                <th class="colsplit" style="font-weight: normal" align="left" valign="top"
                                   width="60%">
                                   ${formData?.special_occasion_request_category_details
                                     .map(
                                       // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                       (speacial: any) => `
                                   <p style="font-size: 14px; mso-line-height-rule: exactly; line-height:
                                      140%; margin: 0px; font-weight: 400; letter-spacing: 0px;
                                      text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular' ,
                                      Arial, Helvetica, sans-serif" class="Swiss-Regular">${speacial}<br>
                                      `
                                     )
                                     .join('')}
                                </th>
                             </tr>
                          </tbody>
                       </table>
                    </td>
                 </tr>
                  <tr class="${formData?.special_occasion_request_other_notes == '' ? 'empty-field' : ''}">
                    <td style="padding: 15px 0px 15px 0px; border-bottom: 1px solid #cccccc;" align="left"
                       valign="top">
                       <table role="presentation" width="100%" border="0" cellspacing="0" cellpadding="0">
                          <tbody>
                             <tr>
                                <th class="colsplit pad10b" style="font-weight: normal; padding-right:
                                   15px" align="left" valign="top" width="40%">
                                   <h4 style="font-size: 16px; mso-line-height-rule: exactly; line-height:
                                      120%; margin: 0px; font-weight: 400; letter-spacing: 0px;
                                      text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular' ,
                                      Arial, Helvetica, sans-serif" class="Swiss-Regular mobile_h4">
                                     <strong>Special Occasion Notes</strong>:
                                   </h4>
                                </th>
                                <th class="colsplit" style="font-weight: normal" align="left" valign="top"
                                   width="60%">

                                   <p style="font-size: 14px; mso-line-height-rule: exactly; line-height:
                                      140%; margin: 0px; font-weight: 400; letter-spacing: 0px;
                                      text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular' ,
                                      Arial, Helvetica, sans-serif" class="Swiss-Regular">${
                                        formData?.special_occasion_request_other_notes
                                      }<br>

                                </th>
                             </tr>
                          </tbody>
                       </table>
                    </td>
                 </tr>
                 <tr class="${formData?.special_occasion_request_notes == '' ? `empty-field` : ``}">
                    <td style="padding: 15px 0px 15px 0px; border-bottom: 1px solid #cccccc;" align="left"
                       valign="top">
                       <table role="presentation" width="100%" border="0" cellspacing="0" cellpadding="0">
                          <tbody>
                             <tr>
                                <th class="colsplit pad10b" style="font-weight: normal; padding-right:
                                   15px" align="left" valign="top" width="40%">
                                   <h4 style="font-size: 16px; mso-line-height-rule: exactly; line-height:
                                      120%; margin: 0px; font-weight: 400; letter-spacing: 0px;
                                      text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular' ,
                                      Arial, Helvetica, sans-serif" class="Swiss-Regular mobile_h4">
                                     <strong>Magical Moment Notes</strong>:
                                   </h4>
                                </th>
                                <th class="colsplit" style="font-weight: normal" align="left" valign="top"
                                   width="60%">

                                   <p style="font-size: 14px; mso-line-height-rule: exactly; line-height:
                                      140%; margin: 0px; font-weight: 400; letter-spacing: 0px;
                                      text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular' ,
                                      Arial, Helvetica, sans-serif" class="Swiss-Regular">${
                                        formData?.special_occasion_request_notes
                                      }<br>

                                </th>
                             </tr>
                          </tbody>
                       </table>
                    </td>
                 </tr>
                  <tr class="${formData?.surprise_request == '' ? 'empty-field' : ''}">
                    <td style="padding: 15px 0px 15px 0px; border-bottom: 1px solid #cccccc;" align="left"
                       valign="top">
                       <table role="presentation" width="100%" border="0" cellspacing="0" cellpadding="0">
                          <tbody>
                             <tr>
                                <th class="colsplit pad10b" style="font-weight: normal; padding-right:
                                   15px" align="left" valign="top" width="40%">
                                   <h4 style="font-size: 16px; mso-line-height-rule: exactly; line-height:
                                      120%; margin: 0px; font-weight: 400; letter-spacing: 0px;
                                      text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular' ,
                                      Arial, Helvetica, sans-serif" class="Swiss-Regular mobile_h4">
                                     <strong>Is this Occasion a surprise</strong>:
                                   </h4>
                                </th>
                                <th class="colsplit" style="font-weight: normal" align="left" valign="top"
                                   width="60%">

                                   <p style="font-size: 14px; mso-line-height-rule: exactly; line-height:
                                      140%; margin: 0px; font-weight: 400; letter-spacing: 0px;
                                      text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular' ,
                                      Arial, Helvetica, sans-serif" class="Swiss-Regular">${
                                        formData?.surprise_request
                                      }<br>

                                </th>
                             </tr>
                          </tbody>
                       </table>
                    </td>
                 </tr>

                  <tr class="${
                    formData?.surprise_request_coordination_contact_detail.length === 0 ? 'empty-field' : ''
                  }">
                    <td style="padding: 15px 0px 15px 0px; border-bottom: 1px solid #cccccc;" align="left"
                        valign="top">
                        <table role="presentation" width="100%" border="0" cellspacing="0" cellpadding="0">
                          <tbody>
                              <tr>
                                <th class="colsplit pad10b" style="font-weight: normal; padding-right:
                                    15px" align="left" valign="top" width="40%">
                                    <h4 style="font-size: 16px; mso-line-height-rule: exactly; line-height:
                                      120%; margin: 0px; font-weight: 400; letter-spacing: 0px;
                                      text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular' ,
                                      Arial, Helvetica, sans-serif" class="Swiss-Regular mobile_h4">
                                      <strong>Surprise Contact Preference</strong>:
                                    </h4>
                                </th>
                                <th class="colsplit" style="font-weight: normal" align="left" valign="top"
                                    width="60%">
                                    ${[
                                      { type: 'email', label: 'Email', checked: soTripCustomerEmail },
                                      { type: 'phone', label: 'Phone Call', checked: sotripPhoneNumber },
                                      { type: 'text', label: 'Text', checked: sotripTextNumber },
                                      { type: 'wechat', label: 'WeChat', checked: sotripWeChatNumber },
                                      { type: 'whatsapp', label: 'WhatsApp', checked: sotripWhatsAppNumber },
                                    ]
                                      .filter(({ checked }) => checked) // Only include selected contact methods
                                      .map(({ type, label }) => {
                                        const detail =
                                          surpriseRequestCoordinationContactDetail.find(
                                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                            (item: any) => item.contact_method_type === type
                                          )?.contact_method_detail ||
                                          (type === 'email' && customerEmail) ||
                                          '';

                                        return `
                                    <p style="font-size: 14px; mso-line-height-rule: exactly; line-height:
                                      140%; margin: 0px; font-weight: 400; letter-spacing: 0px;
                                      text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular' ,
                                      Arial, Helvetica, sans-serif" class="Swiss-Regular">
                                        <strong>${label}: </strong>${detail}
                                      `;
                                      })
                                      .join('')}

                                </th>
                              </tr>
                          </tbody>
                        </table>
                    </td>
                  </tr>
                  <tr class="
                    ${
                      formData?.dining_plan_details.filter((detail: DiningPlanDetail) => detail.reservation_req === '0')
                        .length === 0
                        ? 'empty-field'
                        : ''
                    }">
                  <td style="padding: 15px 0px 15px 0px; border-bottom: 1px solid #cccccc;" align="left"
                        valign="top">
                        <table role="presentation" width="100%" border="0" cellspacing="0" cellpadding="0">
                          <tbody>
                              <tr>
                                <th class="colsplit pad10b" style="font-weight: normal; padding-right:
                                    15px" align="left" valign="top" width="40%">
                                    <h4 style="font-size: 16px; mso-line-height-rule: exactly; line-height:
                                      120%; margin: 0px; font-weight: 400; letter-spacing: 0px;
                                      text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular' ,
                                      Arial, Helvetica, sans-serif" class="Swiss-Regular mobile_h4">
                                      <strong>Dining Added to Your Plan</strong>:
                                    </h4>
                                </th>
                                <th class="colsplit" style="font-weight: normal" align="left" valign="top"
                                    width="60%">
                                    ${formData?.dining_plan_details
                                      .filter((detail: DiningPlanDetail) => detail.reservation_req === '0')
                                      .map(
                                        (detail: DiningPlanDetail) =>
                                          `
                                    <p style="font-size: 14px; mso-line-height-rule: exactly; line-height:
                                      140%; margin: 0px; font-weight: 400; letter-spacing: 0px;
                                      text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular' ,
                                      Arial, Helvetica, sans-serif" class="Swiss-Regular">
                                      ${detail?.dining_location}

                                      `
                                      )
                                      .join('')}
                                </th>
                              </tr>
                          </tbody>
                        </table>
                    </td>

                  </tr>
                  <tr class="${
                    formData?.dining_plan_details?.length > 0 &&
                    formData?.dining_plan_details?.some(
                      (d: DiningPlanDetail) =>
                        d?.reservation_req === '1' &&
                        d?.dining_location &&
                        d?.prefered_date &&
                        d?.prefered_time &&
                        d?.party_size
                    )
                      ? ''
                      : 'empty-field'
                  }">
                    <td style="padding: 15px 0px 15px 0px; border-bottom: 1px solid #cccccc;" align="left"
                        valign="top">
                        <table role="presentation" width="100%" border="0" cellspacing="0" cellpadding="0">
                          <tbody>
                              <tr>
                                <th class="colsplit pad10b" style="font-weight: normal; padding-right:
                                    15px" align="left" valign="top" width="40%">
                                    <h4 style="font-size: 16px; mso-line-height-rule: exactly; line-height:
                                      120%; margin: 0px; font-weight: 400; letter-spacing: 0px;
                                      text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular' ,
                                      Arial, Helvetica, sans-serif" class="Swiss-Regular mobile_h4">
                                      <strong>Dining Reservations Added to Your Plan</strong>:
                                    </h4>
                                </th>
                                <th class="colsplit" style="font-weight: normal" align="left" valign="top"
                                    width="60%">
                                    ${formData?.dining_plan_details
                                      .filter((detail: DiningPlanDetail) => detail.reservation_req === '1')
                                      .map(
                                        (dining: DiningPlanDetail) => `
                                    <p style="font-size: 14px; mso-line-height-rule: exactly; line-height:
                                      140%; margin: 0px; font-weight: 400; letter-spacing: 0px;
                                      text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular' ,
                                      Arial, Helvetica, sans-serif" class="Swiss-Regular">
                                      <strong>${dining?.dining_location}</strong>
                                      <br>${dining?.prefered_date} ${dining?.prefered_time},  Party for ${dining?.party_size} (Preferred)
                                      <br>${dining?.alternate_date} ${dining?.alternate_time}, Party for ${dining?.party_size} (Alternative)
                                      <br>
                                      `
                                      )
                                      .join('')}
                                </th>
                              </tr>
                          </tbody>
                        </table>
                    </td>
                  </tr>
                  <tr class="${formData?.other_dining_details.length === 0 ? 'empty-field' : ''}">
                    <td style="padding: 15px 0px 15px 0px; border-bottom: 1px solid #cccccc;" align="left"
                        valign="top">
                        <table role="presentation" width="100%" border="0" cellspacing="0" cellpadding="0">
                          <tbody>
                              <tr>
                                <th class="colsplit pad10b" style="font-weight: normal; padding-right:
                                    15px" align="left" valign="top" width="40%">
                                    <h4 style="font-size: 16px; mso-line-height-rule: exactly; line-height:
                                      120%; margin: 0px; font-weight: 400; letter-spacing: 0px;
                                      text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular' ,
                                      Arial, Helvetica, sans-serif" class="Swiss-Regular mobile_h4">
                                      <strong>${headingText} Dining Experiences</strong>:
                                    </h4>
                                </th>
                                <th class="colsplit" style="font-size: 14px; mso-line-height-rule: exactly; line-height:
                                      140%; margin: 0px; font-weight: 400; letter-spacing: 0px;
                                      text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular' ,
                                      Arial, Helvetica, sans-serif" class="Swiss-Regular" align="left" valign="top"
                                      width="60%">
                                      ${getLabels(formData?.other_dining_details)}
                                </th>
                              </tr>
                          </tbody>
                        </table>
                    </td>
                  </tr>
                  <tr class="${formData?.dining_notes == '' ? 'empty-field' : ''}">
                    <td style="padding: 15px 0px 15px 0px; border-bottom: 1px solid #cccccc;" align="left"
                        valign="top">
                        <table role="presentation" width="100%" border="0" cellspacing="0" cellpadding="0">
                          <tbody>
                              <tr>
                                <th class="colsplit pad10b" style="font-weight: normal; padding-right:
                                    15px" align="left" valign="top" width="40%">
                                    <h4 style="font-size: 16px; mso-line-height-rule: exactly; line-height:
                                      120%; margin: 0px; font-weight: 400; letter-spacing: 0px;
                                      text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular' ,
                                      Arial, Helvetica, sans-serif" class="Swiss-Regular mobile_h4">
                                      <strong>Dining Experiences Notes</strong>:
                                    </h4>
                                </th>
                                <th class="colsplit" style="font-weight: normal" align="left" valign="top"
                                    width="60%">
                                    <p style="font-size: 14px; mso-line-height-rule: exactly; line-height:
                                                140%; margin: 0px; font-weight: 400; letter-spacing: 0px;
                                                text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular' ,
                                                Arial, Helvetica, sans-serif" class="Swiss-Regular">
                                                ${formData?.dining_notes}


                                </th>
                              </tr>
                          </tbody>
                        </table>
                    </td>
                  </tr>
                  <tr class="${formData?.dietary_restrictions_details.length === 0 ? 'empty-field' : ''}">
                    <td style="padding: 15px 0px 15px 0px; border-bottom: 1px solid #cccccc;" align="left"
                        valign="top">
                        <table role="presentation" width="100%" border="0" cellspacing="0" cellpadding="0">
                          <tbody>
                              <tr>
                                <th class="colsplit pad10b" style="font-weight: normal; padding-right:
                                    15px" align="left" valign="top" width="40%">
                                    <h4 style="font-size: 16px; mso-line-height-rule: exactly; line-height:
                                      120%; margin: 0px; font-weight: 400; letter-spacing: 0px;
                                      text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular' ,
                                      Arial, Helvetica, sans-serif" class="Swiss-Regular mobile_h4">
                                      <strong>Dietary Restrictions/Preferences</strong>:
                                    </h4>
                                </th>
                                <th class="colsplit" style="font-size: 14px; mso-line-height-rule: exactly; line-height:
                                      140%; margin: 0px; font-weight: 400; letter-spacing: 0px;
                                      text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular' ,
                                      Arial, Helvetica, sans-serif" class="Swiss-Regular" align="left" valign="top"
                                      width="60%">
                                      ${getLabels(formData?.dietary_restrictions_details)}
                                </th>
                              </tr>
                          </tbody>
                        </table>
                    </td>
                  </tr>
                  <tr class="${formData?.dining_dietary_restrictions_notes == '' ? 'empty-field' : ''}">
                    <td style="padding: 15px 0px 15px 0px; border-bottom: 1px solid #cccccc;" align="left"
                        valign="top">
                        <table role="presentation" width="100%" border="0" cellspacing="0" cellpadding="0">
                          <tbody>
                              <tr>
                                <th class="colsplit pad10b" style="font-weight: normal; padding-right:
                                    15px" align="left" valign="top" width="40%">
                                    <h4 style="font-size: 16px; mso-line-height-rule: exactly; line-height:
                                      120%; margin: 0px; font-weight: 400; letter-spacing: 0px;
                                      text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular' ,
                                      Arial, Helvetica, sans-serif" class="Swiss-Regular mobile_h4">
                                      <strong>Dietary Preferences Notes</strong>:
                                    </h4>
                                </th>
                                <th class="colsplit" style="font-weight: normal" align="left" valign="top"
                                    width="60%">
                                    <p style="font-size: 14px; mso-line-height-rule: exactly; line-height:
                                                140%; margin: 0px; font-weight: 400; letter-spacing: 0px;
                                                text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular' ,
                                                Arial, Helvetica, sans-serif" class="Swiss-Regular">
                                                ${formData?.dining_dietary_restrictions_notes}


                                </th>
                              </tr>
                          </tbody>
                        </table>
                    </td>
                  </tr>
                  <tr class="${
                    formData?.experiences_plan_details?.some(
                      (detail: DiningPlanDetail) => detail?.reservation_req === '0' && detail?.dining_location
                    )
                      ? ''
                      : 'empty-field'
                  }">
 
                    <td style="padding: 15px 0px 15px 0px; border-bottom: 1px solid #cccccc;" align="left"
                        valign="top">
                        <table role="presentation" width="100%" border="0" cellspacing="0" cellpadding="0">
                          <tbody>
                              <tr>
                                <th class="colsplit pad10b" style="font-weight: normal; padding-right:
                                    15px" align="left" valign="top" width="40%">
                                    <h4 style="font-size: 16px; mso-line-height-rule: exactly; line-height:
                                      120%; margin: 0px; font-weight: 400; letter-spacing: 0px;
                                      text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular' ,
                                      Arial, Helvetica, sans-serif" class="Swiss-Regular mobile_h4">
                                      <strong>Activities & Experiences Added to Your Plan</strong>:
                                    </h4>
                                </th>
                                <th class="colsplit" style="font-weight: normal" align="left" valign="top"
                                    width="60%">
                                    ${formData?.experiences_plan_details
                                      .filter((detail: DiningPlanDetail) => detail?.reservation_req === '0')
                                      .map(
                                        (experience: DiningPlanDetail) =>
                                          `
                                    <p style="font-size: 14px; mso-line-height-rule: exactly; line-height:
                                      140%; margin: 0px; font-weight: 400; letter-spacing: 0px;
                                      text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular' ,
                                      Arial, Helvetica, sans-serif" class="Swiss-Regular">
                                      ${experience?.dining_location}
                                      `
                                      )
                                      .join('')}
                                </th>
                              </tr>

                          </tbody>
                        </table>
                    </td>
                  </tr>
                  <tr class="${
                    formData?.experiences_plan_details?.filter(
                      (detail: DiningPlanDetail) => detail?.reservation_req === '1'
                    ).length > 0
                      ? ''
                      : 'empty-field'
                  }">
                    <td style="padding: 15px 0px 15px 0px; border-bottom: 1px solid #cccccc;" align="left"
                        valign="top">
                        <table role="presentation" width="100%" border="0" cellspacing="0" cellpadding="0">
                          <tbody>
                              <tr>
                                <th class="colsplit pad10b" style="font-weight: normal; padding-right:
                                    15px" align="left" valign="top" width="40%">
                                    <h4 style="font-size: 16px; mso-line-height-rule: exactly; line-height:
                                      120%; margin: 0px; font-weight: 400; letter-spacing: 0px;
                                      text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular' ,
                                      Arial, Helvetica, sans-serif" class="Swiss-Regular mobile_h4">
                                      <strong>Activities & Experiences Reservation Added to Your Plan</strong>:
                                    </h4>
                                </th>
                                <th class="colsplit" style="font-weight: normal" align="left" valign="top"
                                    width="60%">
                                    ${formData?.experiences_plan_details
                                      .filter((detail: DiningPlanDetail) => detail.reservation_req === '1')
                                      .map(
                                        (experience: DiningPlanDetail) =>
                                          `
                                    <p style="font-size: 14px; mso-line-height-rule: exactly; line-height:
                                      140%; margin: 0px; font-weight: 400; letter-spacing: 0px;
                                      text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular' ,
                                      Arial, Helvetica, sans-serif" class="Swiss-Regular">
                                      <strong>${experience?.dining_location}</strong>
                                      <br> ${experience?.prefered_date} ${experience?.prefered_time}, Party for ${experience?.party_size} (Preferred)
                                      <br>${experience?.alternate_date} ${experience?.alternate_time}, Party for ${experience?.party_size} (Alternative)
                                      <br>
                                      `
                                      )
                                      .join('')}
                                </th>
                              </tr>

                          </tbody>
                        </table>
                    </td>
                  </tr>
                  <tr class="${formData?.other_experiences_details.length === 0 ? 'empty-field' : ''}">
                    <td style="padding: 15px 0px 15px 0px; border-bottom: 1px solid #cccccc;" align="left"
                        valign="top">
                        <table role="presentation" width="100%" border="0" cellspacing="0" cellpadding="0">
                          <tbody>
                              <tr>
                                <th class="colsplit pad10b" style="font-weight: normal; padding-right:
                                    15px" align="left" valign="top" width="40%">
                                    <h4 style="font-size: 16px; mso-line-height-rule: exactly; line-height:
                                      120%; margin: 0px; font-weight: 400; letter-spacing: 0px;
                                      text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular' ,
                                      Arial, Helvetica, sans-serif" class="Swiss-Regular mobile_h4">
                                      <strong>${headingExperienceText} Activities and Experiences</strong>:
                                    </h4>
                                </th>
                                <th class="colsplit" style="font-size: 14px; mso-line-height-rule: exactly; line-height:
                                      140%; margin: 0px; font-weight: 400; letter-spacing: 0px;
                                      text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular' ,
                                      Arial, Helvetica, sans-serif" class="Swiss-Regular" align="left" valign="top"
                                    width="60%">

                                      ${getLabels(formData?.other_experiences_details)}
                                </th>
                              </tr>

                          </tbody>
                        </table>
                    </td>
                  </tr>
                  <tr class="${formData?.dietary_restrictions_notes == '' ? 'empty-field' : ''}">
                    <td style="padding: 15px 0px 15px 0px; border-bottom: 1px solid #cccccc;" align="left"
                        valign="top">
                        <table role="presentation" width="100%" border="0" cellspacing="0" cellpadding="0">
                          <tbody>
                              <tr>
                                <th class="colsplit pad10b" style="font-weight: normal; padding-right:
                                    15px" align="left" valign="top" width="40%">
                                    <h4 style="font-size: 16px; mso-line-height-rule: exactly; line-height:
                                      120%; margin: 0px; font-weight: 400; letter-spacing: 0px;
                                      text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular' ,
                                      Arial, Helvetica, sans-serif" class="Swiss-Regular mobile_h4">
                                      <strong>Additional Opportunities Notes</strong>:
                                    </h4>
                                </th>
                                <th class="colsplit" style="font-weight: normal" align="left" valign="top"
                                    width="60%">
                                    <p style="font-size: 14px; mso-line-height-rule: exactly; line-height:
                                                140%; margin: 0px; font-weight: 400; letter-spacing: 0px;
                                                text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular' ,
                                                Arial, Helvetica, sans-serif" class="Swiss-Regular">
                                                ${formData?.dietary_restrictions_notes}


                                </th>
                              </tr>
                          </tbody>
                        </table>
                    </td>
                  </tr>

                </table>
            </td>
          </tr>
        </tbody>
  </table>
</table>`;

      // Web-optimized confirmation HTML — flat labeled text with <b> labels and <br>
      // section breaks. Sections with no data are omitted entirely.
      const confirmation_html_optimized = (() => {
        const s = (v: any) => v ?? '';
        const sections: string[] = [];

        // Helper: build a guest name string
        const guestName = (g: any) => {
          const parts = [g.title, g.guest_first_name, g.guest_last_name].filter(Boolean);
          const name = parts.join(' ');
          return g.relationship_type ? (name ? `${name}, ${g.relationship_type}` : g.relationship_type) : name;
        };

        // Helper: build a child string
        const childStr = (c: any) => {
          const name = [c.childFirstName, c.childLastName].filter(Boolean).join(' ');
          const age = c.childAge
            ? `${name ? ', ' : ''}${
                c.childAge === '<1' || c.childAge == '1' || c.childAge < 2
                  ? `${c.childAge} Year`
                  : `${c.childAge} Years`
              }`
            : '';
          const notes = c.childNotesArea ? `${name || age ? ', ' : ''}${c.childNotesArea}` : '';
          return `${name}${age}${notes}`;
        };

        // Helper: build a pet string
        const petStr = (p: any) => {
          const name = [p.petName, p.petBreed].filter(Boolean).join(', ');
          const notes = p.petNotesArea ? `${name ? ', ' : ''}${p.petNotesArea}` : '';
          return `${name}${notes}`;
        };

        // Helper: numbered inline list "1. a 2. b"
        const numbered = (items: string[]) => items.map((item, i) => `${i + 1}. ${item}`).join(' ');

        // Helper: preferred vs alternate dining/experience party-size lines.
        // Preferred always uses the guest-selected party_size. Alternate line is
        // omitted unless an alternate date/time was entered.
        const partySizeLines = (d: DiningPlanDetail) => {
          const preferredSize = d.party_size;
          const preferred = [d.prefered_date, d.prefered_time].filter(Boolean).join(' ');
          const alternate = [d.alternate_date, d.alternate_time].filter(Boolean).join(' ');
          const lines: string[] = [];
          if (preferred) {
            lines.push(`${preferred}, ${preferredSize} (Preferred)`);
          }
          if (d.alternate_date) {
            lines.push(`${alternate}, ${preferredSize} (Alternative)`);
          }
          return lines.join('<br>');
        };

        // 1. Reservation
        if (formData?.reservation_num || formData?.reservation?.arrival || formData?.reservation?.departure) {
          sections.push(
            `<b>Reservation #:</b> ${s(formData?.reservation_num)}<br><b>Check-In:</b> ${s(
              formData?.reservation?.arrival
            )}<br><b>Check-Out:</b> ${s(formData?.reservation?.departure)}`
          );
        }

        // 2. Guest Information
        if (formData?.reservation?.customerFirstName || formData?.reservation?.customerLastName) {
          sections.push(
            `<b>Guest Information:</b> ${s(formData?.reservation?.customerFirstName)} ${s(
              formData?.reservation?.customerLastName
            )}`
          );
        }

        // 3. Travel Companion Details
        const hasGuests = formData?.additional_guests_details?.length > 0;
        const hasChildren = formData?.children_details?.length > 0;
        const hasPets = formData?.pet_details?.length > 0;
        if (hasGuests || hasChildren || hasPets) {
          const lines: string[] = ['<b>TRAVEL COMPANION DETAILS</b>'];
          if (hasGuests) {
            lines.push(`<b>Guest:</b> ${numbered(formData.additional_guests_details.map((g: any) => guestName(g)))}`);
          }
          if (hasChildren) {
            lines.push(`<b>Child:</b> ${numbered(formData.children_details.map((c: any) => childStr(c)))}`);
          }
          if (hasPets) {
            lines.push(`<b>Pet:</b> ${numbered(formData.pet_details.map((p: any) => petStr(p)))}`);
          }
          sections.push(lines.join('<br>'));
        }

        // 4. Contact Preferences
        const contactItems = [
          { type: 'email', label: 'Email', checkbox: tripEmailCheckbox },
          { type: 'phone', label: 'Phone', checkbox: tripPhoneCheckbox },
          { type: 'text', label: 'Text', checkbox: tripTextCheckbox },
          { type: 'wechat', label: 'WeChat', checkbox: tripWechatCheckbox },
          { type: 'whatsapp', label: 'Whatsapp', checkbox: tripWhatsappCheckbox },
        ].filter(({ checkbox }) => checkbox);
        if (contactItems.length > 0) {
          const contactLines = contactItems
            .map(({ type, label }) => {
              const detail =
                preferredContactMethodDetails.find((item: any) => item.contact_method_type === type)
                  ?.contact_method_detail ||
                (type === 'email' && customerEmail) ||
                '';
              const tz =
                preferredContactMethodDetails.find((item: any) => item.contact_method_type === type)
                  ?.timezone_of_guest || '';
              if (!detail) return '';
              return `<b>${label}:</b> ${detail}${type !== 'email' && tz ? `, ${tz}` : ''}`;
            })
            .filter(Boolean);
          if (contactLines.length > 0) {
            sections.push(`<b>CONTACT PREFERENCES</b><br>${contactLines.join('<br>')}`);
          }
        }

        // 5. Accessibility Needs (+ Arrival Anticipate Time)
        // Only show accessibility/disability fields when the guest actually entered them.
        const accDetails = (formData?.accessiblity_details || []).filter(Boolean);
        const accNotes = typeof formData?.accessibility_notes === 'string' ? formData.accessibility_notes.trim() : '';
        const hasAccessibilityNeeds = formData?.accessiblity_needs !== 'No' && (accDetails.length > 0 || !!accNotes);
        const hasArrivalAnticipate = !!formData?.arrival_anticipate_time;
        if (hasAccessibilityNeeds || hasArrivalAnticipate) {
          const lines: string[] = [];
          if (hasAccessibilityNeeds) {
            lines.push('<b>ACCESSIBILITY NEEDS</b>');
            const accParts = [...accDetails, accNotes].filter(Boolean);
            if (accParts.length > 0) {
              lines.push(`<b>Accessibility Note:</b> ${accParts.join(' — ')}`);
            }
          }
          if (hasArrivalAnticipate) {
            lines.push(`<b>Arrival Anticipate Time:</b> ${formData.arrival_anticipate_time}`);
          }
          sections.push(lines.join('<br>'));
        }

        // 6. Arrival Information (includes Departure + Assist Notes)
        const hasArrival =
          formData?.arrival_method ||
          formData?.transportation_arrival_date ||
          formData?.transportation_arrival_time ||
          formData?.arrivalInfo ||
          formData?.arrivalFlightNumber ||
          formData?.arrivalPassengers;
        const hasDeparture =
          formData?.departure_method ||
          formData?.transportation_departure_date ||
          formData?.transportation_departure_time ||
          formData?.departureInfo ||
          formData?.departureFlightNumber ||
          formData?.departurePassengers;
        const hasAssistNotes = !!formData?.arrival_dept_notes;
        if (hasArrival || hasDeparture || hasAssistNotes) {
          const lines: string[] = ['<b>ARRIVAL INFORMATION</b>'];
          if (formData?.arrival_method) lines.push(`<b>Arrival Method:</b> ${formData.arrival_method}`);
          if (formData?.transportation_arrival_date)
            lines.push(`<b>Arrival Date:</b> ${formData.transportation_arrival_date}`);
          if (formData?.transportation_arrival_time)
            lines.push(`<b>Arrival Timing:</b> ${formData.transportation_arrival_time}`);
          if (formData?.arrivalInfo) lines.push(`<b>Arrival Location:</b> ${formData.arrivalInfo}`);
          if (formData?.arrivalFlightNumber)
            lines.push(`<b>Arrival Location Details:</b> ${formData.arrivalFlightNumber}`);
          if (formData?.arrivalPassengers)
            lines.push(`<b>Number of Passengers on Arrival:</b> ${formData.arrivalPassengers}`);
          lines.push('<b>Departure Information:</b>');
          if (formData?.departure_method) lines.push(`<b>Departure Method:</b> ${formData.departure_method}`);
          if (formData?.transportation_departure_date)
            lines.push(`<b>Departure Date:</b> ${formData.transportation_departure_date}`);
          if (formData?.transportation_departure_time)
            lines.push(`<b>Departure Timing:</b> ${formData.transportation_departure_time}`);
          if (formData?.departureInfo) lines.push(`<b>Departure Location:</b> ${formData.departureInfo}`);
          if (formData?.departureFlightNumber)
            lines.push(`<b>Departure Location Details:</b> ${formData.departureFlightNumber}`);
          if (formData?.departurePassengers)
            lines.push(`<b>Number of Passengers on Departure:</b> ${formData.departurePassengers}`);
          if (formData?.arrival_dept_notes) lines.push(`<b>Assist Notes:</b> ${formData.arrival_dept_notes}`);
          sections.push(lines.join('<br>'));
        }

        // 7. Additional Travel Details
        if (formData?.additional_requests) {
          sections.push(`<b>Additional Travel Details:</b> ${formData.additional_requests}`);
        }

        // 8. Special Occasions
        const hasOccasion =
          formData?.special_occasion_request_category_details?.length > 0 ||
          formData?.special_occasion_request_other_notes ||
          formData?.special_occasion_request_notes ||
          formData?.surprise_request ||
          soTripCustomerEmail ||
          sotripPhoneNumber ||
          sotripTextNumber ||
          sotripWeChatNumber ||
          sotripWhatsAppNumber;
        if (hasOccasion) {
          const lines: string[] = ['<b>SPECIAL OCCASIONS</b>'];
          if (formData?.special_occasion_request_category_details?.length > 0) {
            lines.push(formData.special_occasion_request_category_details.join(', '));
          }
          if (formData?.special_occasion_request_other_notes) {
            lines.push(`<b>Special Occasion Notes:</b> ${formData.special_occasion_request_other_notes}`);
          }
          lines.push(`<b>Magical Moment Notes:</b> ${s(formData?.special_occasion_request_notes)}`);
          if (formData?.surprise_request) {
            lines.push(`<b>Is this Occasion a surprise:</b> ${formData.surprise_request}`);
          }
          const surpriseItems = [
            { type: 'email', label: 'Email', checked: soTripCustomerEmail },
            { type: 'phone', label: 'Phone', checked: sotripPhoneNumber },
            { type: 'text', label: 'Text', checked: sotripTextNumber },
            { type: 'wechat', label: 'WeChat', checked: sotripWeChatNumber },
            { type: 'whatsapp', label: 'Whatsapp', checked: sotripWhatsAppNumber },
          ].filter(({ checked }) => checked);
          if (surpriseItems.length > 0) {
            const surpriseDetail = surpriseItems
              .map(({ type, label }) => {
                const detail =
                  surpriseRequestCoordinationContactDetail.find((item: any) => item.contact_method_type === type)
                    ?.contact_method_detail ||
                  (type === 'email' && customerEmail) ||
                  '';
                return `${label}: ${detail}`;
              })
              .join('; ');
            lines.push(`<b>Surprise Contact Preference:</b> ${surpriseDetail}`);
          }
          sections.push(lines.join('<br>'));
        }

        // 9. Requested Dining Added to Plan
        const diningNonRes =
          formData?.dining_plan_details?.filter((d: DiningPlanDetail) => d.reservation_req === '0') || [];
        const diningRes =
          formData?.dining_plan_details?.filter(
            (d: DiningPlanDetail) =>
              d.reservation_req === '1' && d.dining_location && d.prefered_date && d.prefered_time && d.party_size
          ) || [];
        const hasDining =
          diningNonRes.length > 0 ||
          diningRes.length > 0 ||
          formData?.other_dining_details?.length > 0 ||
          formData?.dining_notes ||
          formData?.dietary_restrictions_details?.length > 0 ||
          formData?.dining_dietary_restrictions_notes;
        if (hasDining) {
          const lines: string[] = ['<b>REQUESTED DINING ADDED TO PLAN</b>'];
          const diningSelections = [
            ...diningNonRes.map((d: DiningPlanDetail) => d.dining_location),
            ...diningRes.map((d: DiningPlanDetail) => d.dining_location),
          ].filter(Boolean);
          if (diningSelections.length > 0) {
            lines.push(`<b>Dining Reservations Added to Guest Plan:</b> ${diningSelections.join(', ')}`);
          }
          diningRes.forEach((d: DiningPlanDetail) => {
            lines.push(`${d.dining_location}`);
            const sizeLines = partySizeLines(d);
            if (sizeLines) {
              lines.push(sizeLines);
            }
          });
          if (formData?.other_dining_details?.length > 0) {
            lines.push(`<b>Other Dining Experiences:</b> ${getLabels(formData.other_dining_details)}`);
          }
          if (formData?.dining_notes) {
            lines.push(`<b>Dining Experiences Notes:</b> ${formData.dining_notes}`);
          }
          if (formData?.dietary_restrictions_details?.length > 0) {
            lines.push(`<b>Dietary Restrictions/Preferences:</b> ${getLabels(formData.dietary_restrictions_details)}`);
          }
          if (formData?.dining_dietary_restrictions_notes) {
            lines.push(`<b>Dietary Preferences Notes:</b> ${formData.dining_dietary_restrictions_notes}`);
          }
          sections.push(lines.join('<br>'));
        }

        // 10. Requested Activities & Experiences
        const expNonRes =
          formData?.experiences_plan_details?.filter(
            (d: DiningPlanDetail) => d.reservation_req === '0' && d.dining_location
          ) || [];
        const expRes =
          formData?.experiences_plan_details?.filter((d: DiningPlanDetail) => d.reservation_req === '1') || [];
        const hasExperiences =
          expNonRes.length > 0 ||
          expRes.length > 0 ||
          formData?.other_experiences_details?.length > 0 ||
          formData?.dietary_restrictions_notes;
        if (hasExperiences) {
          const lines: string[] = ['<b>REQUESTED ACTIVITIES & EXPERIENCES</b>'];
          const expSelections = [
            ...expNonRes.map((e: DiningPlanDetail) => e.dining_location),
            ...expRes.map((e: DiningPlanDetail) => e.dining_location),
          ].filter(Boolean);
          if (expSelections.length > 0) {
            lines.push(`<b>Activities & Experiences Reservation Added to Your Plan:</b> ${expSelections.join(', ')}`);
          }
          expRes.forEach((e: DiningPlanDetail) => {
            lines.push(`${e.dining_location}`);
            const sizeLines = partySizeLines(e);
            if (sizeLines) {
              lines.push(sizeLines);
            }
          });
          if (formData?.other_experiences_details?.length > 0) {
            lines.push(`<b>Other Activities and Experiences:</b> ${getLabels(formData.other_experiences_details)}`);
          }
          if (formData?.dietary_restrictions_notes) {
            lines.push(`<b>Additional Opportunities Notes:</b> ${formData.dietary_restrictions_notes}`);
          }
          sections.push(lines.join('<br>'));
        }

        return sections.join('<br><br>');
      })();

      const user_detail_html = `<table class="table">
        <table role"presentation" width="100%" border="0" cellspacing="0" cellpadding="0" bgcolor="#ffffff">
            <tbody>
                <tr>
                    <td style="padding: 0px 40px 0px 40px" class="pad20rl">
                        <table role="presentation" width="100%" border="0" cellspacing="0" cellpadding="0">
                            <tr>
                                <td style="padding: 15px 0px 15px 0px; border-bottom: 1px solid #cccccc;" align="left" valign="top">
                                    <table role="presentation" width="100%" border="0" cellspacing="0" cellpadding="0">
                                        <tbody>
                                            <tr>
                                                <th class="colsplit pad10b" style="font-weight: normal; padding-right:
                                      15px" align="left" valign="top" width="40%">
                                                    <h4 style="font-size: 16px; mso-line-height-rule: exactly; line-height:
                                          120%; margin: 0px; font-weight: 400; letter-spacing: 0px;
                                          text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular' ,
                                          Arial, Helvetica, sans-serif" class="Swiss-Regular mobile_h4">
                                                        <strong>First Name</strong>:
                                                    </h4>
                                                </th>
                                                <th class="colsplit" style="font-weight: normal" align="left" valign="top" width="60%">
                                                    <span style="font-size: 14px; mso-line-height-rule: exactly; line-height:
                                          140%; margin: 0px; font-weight: 400; letter-spacing: 0px;
                                          text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular' ,
                                          Arial, Helvetica, sans-serif" class="Swiss-Regular"> 
                                                      ${formData?.reservation.customerFirstName}
                                                    </span>
                                                </th>
                                            </tr>
                                        </tbody>
                                    </table>
                                </td>
                            </tr>
                        </table>
                    </td>
                </tr>
                <tr>
                    <td style="padding: 0px 40px 0px 40px" class="pad20rl">
                        <table role="presentation" width="100%" border="0" cellspacing="0" cellpadding="0">
                            <tr>
                                <td style="padding: 15px 0px 15px 0px; border-bottom: 1px solid #cccccc;" align="left" valign="top">
                                    <table role="presentation" width="100%" border="0" cellspacing="0" cellpadding="0">
                                        <tbody>
                                            <tr>
                                                <th class="colsplit pad10b" style="font-weight: normal; padding-right:
                                      15px" align="left" valign="top" width="40%">
                                                    <h4 style="font-size: 16px; mso-line-height-rule: exactly; line-height:
                                          120%; margin: 0px; font-weight: 400; letter-spacing: 0px;
                                          text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular' ,
                                          Arial, Helvetica, sans-serif" class="Swiss-Regular mobile_h4">
                                                        <strong>Last Name</strong>:
                                                    </h4>
                                                </th>
                                                <th class="colsplit" style="font-weight: normal" align="left" valign="top" width="60%">
                                                    <span style="font-size: 14px; mso-line-height-rule: exactly; line-height:
                                          140%; margin: 0px; font-weight: 400; letter-spacing: 0px;
                                          text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular' ,
                                          Arial, Helvetica, sans-serif" class="Swiss-Regular"> 
                                                      ${formData?.reservation.customerLastName}
                                                    </span>
                                                </th>
                                            </tr>
                                        </tbody>
                                    </table>
                                </td>
                            </tr>
                        </table>
                    </td>
                </tr>
                <tr>
                    <td style="padding: 0px 40px 0px 40px" class="pad20rl">
                        <table role="presentation" width="100%" border="0" cellspacing="0" cellpadding="0">
                            <tr>
                                <td style="padding: 15px 0px 15px 0px; border-bottom: 1px solid #cccccc;" align="left" valign="top">
                                    <table role="presentation" width="100%" border="0" cellspacing="0" cellpadding="0">
                                        <tbody>
                                            <tr>
                                                <th class="colsplit pad10b" style="font-weight: normal; padding-right:
                                                  15px" align="left" valign="top" width="40%">
                                                  <h4 style="font-size: 16px; mso-line-height-rule: exactly; line-height:
                                                      120%; margin: 0px; font-weight: 400; letter-spacing: 0px;
                                                      text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular' ,
                                                      Arial, Helvetica, sans-serif" class="Swiss-Regular mobile_h4">
                                                      <strong>Reservation</strong>:
                                                  </h4>
                                                </th>
                                                <th class="colsplit" style="font-weight: normal" align="left" valign="top"
                                                  width="60%">
                                                  <span style="font-size: 14px; mso-line-height-rule: exactly; line-height:
                                                      140%; margin: 0px; font-weight: 400; letter-spacing: 0px;
                                                      text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular' ,
                                                      Arial, Helvetica, sans-serif" class="Swiss-Regular">
                                                      <strong class="${
                                                        formData?.reservation_num == '' ? 'empty-field' : ''
                                                      }">
                                                        Reservation #: 
                                                      </strong class="${
                                                        formData?.reservation?.arrival == '' ? 'empty-field' : ''
                                                      }">
                                                        ${formData?.reservation_num}
                                                      <br>
                                                  </span>
                                                </th>
                                            </tr>
                                        </tbody>
                                    </table>
                                </td>
                            </tr>
                        </table>
                    </td>
                </tr>
                <tr>
                    <td style="padding: 0px 40px 0px 40px" class="pad20rl">
                        <table role="presentation" width="100%" border="0" cellspacing="0" cellpadding="0">
                            <tr>
                                <td style="padding: 15px 0px 15px 0px; border-bottom: 1px solid #cccccc;" align="left" valign="top">
                                    <table role="presentation" width="100%" border="0" cellspacing="0" cellpadding="0">
                                        <tbody>
                                            <tr>
                                                <th class="colsplit pad10b" style="font-weight: normal; padding-right: 15px" align="left" valign="top" width="40%">
                                                  <h4 style="font-size: 16px; mso-line-height-rule: exactly; line-height: 120%; margin: 0px; font-weight: 400; letter-spacing: 0px; text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular', Arial, Helvetica, sans-serif" class="Swiss-Regular mobile_h4">
                                                    <strong>Departure Date</strong>:
                                                  </h4>
                                                </th>
                                                <th class="colsplit" style="font-weight: normal" align="left" valign="top" width="60%">
                                                  <span style="font-size: 14px; mso-line-height-rule: exactly; line-height: 140%; margin: 0px; font-weight: 400; letter-spacing: 0px; text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular', Arial, Helvetica, sans-serif" class="Swiss-Regular">
                                                    <strong class="${
                                                      formData?.transportation_departure_date == '' ? 'empty-field' : ''
                                                    }">Departure Date: </strong>${
        formData?.transportation_departure_date
      }<br>
                                                  </span>
                                                </th>
                                            </tr>
                                        </tbody>
                                    </table>
                                </td>
                            </tr>
                        </table>
                    </td>
                </tr>
                <tr>
                    <td style="padding: 0px 40px 0px 40px" class="pad20rl">
                        <table role="presentation" width="100%" border="0" cellspacing="0" cellpadding="0">
                            <tr>
                                <td style="padding: 15px 0px 15px 0px; border-bottom: 1px solid #cccccc;" align="left" valign="top">
                                    <table role="presentation" width="100%" border="0" cellspacing="0" cellpadding="0">
                                        <tbody>
                                            <tr>
                                                <th class="colsplit pad10b" style="font-weight: normal; padding-right: 15px" align="left" valign="top" width="40%">
                                                  <h4 style="font-size: 16px; mso-line-height-rule: exactly; line-height: 120%; margin: 0px; font-weight: 400; letter-spacing: 0px; text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular', Arial, Helvetica, sans-serif" class="Swiss-Regular mobile_h4">
                                                    <strong>Arrival Date</strong>:
                                                  </h4>
                                                </th>
                                                <th class="colsplit" style="font-weight: normal" align="left" valign="top" width="60%">
                                                  <span style="font-size: 14px; mso-line-height-rule: exactly; line-height: 140%; margin: 0px; font-weight: 400; letter-spacing: 0px; text-transform: none; color: #1c1c1c; font-family: 'Swiss-Regular', Arial, Helvetica, sans-serif" class="Swiss-Regular">
                                                    <strong class="${
                                                      formData?.transportation_arrival_date == '' ? 'empty-field' : ''
                                                    }">Arrival Date: </strong>${
        formData?.transportation_arrival_date
      }<br>
                                                  </span>
                                                </th>
                                            </tr>
                                        </tbody>
                                    </table>
                                </td>
                            </tr>
                        </table>
                    </td>
                </tr>
            </tbody>
        </table>
      </table>`;

      // Add Data That need to send to Response

      const arrivalMethodTransport: any = {
        number_of_passengers_on_arrival: formData?.arrivalPassengers || '',
      };

      if (formData.arrival_method === 'Private Plane' || formData.arrival_method === 'Commercial Airplane') {
        arrivalMethodTransport.arrival_location = formData?.arrivalInfo || '';
        arrivalMethodTransport.arrival_location_details = formData?.arrivalFlightNumber || '';
      } else if (formData.arrival_method === 'Train') {
        arrivalMethodTransport.arrival_location = formData?.arrivalInfo || '';
        arrivalMethodTransport.arrival_location_details = formData?.arrivalFlightNumber || '';
      } else {
        // Car / Other Local Destination
        arrivalMethodTransport.arrival_location = formData?.arrivalInfo || '';
      }

      const departureMethodTransport: any = {
        number_of_passengers_on_departure: formData?.departurePassengers || '',
      };

      if (formData.departure_method === 'Private Plane' || formData.departure_method === 'Commercial Airplane') {
        departureMethodTransport.departure_location = formData?.departureInfo || '';
        departureMethodTransport.departure_location_details = formData?.departureFlightNumber || '';
      } else if (formData.departure_method === 'Train') {
        departureMethodTransport.departure_location = formData?.departureInfo || '';
        departureMethodTransport.departure_location_details = formData?.departureFlightNumber || '';
      } else {
        // Car / Other Local Destination
        departureMethodTransport.departure_location = formData?.departureInfo || '';
      }

      // data to send in payload
      const formDataToSend = {
        marsha_cd: formData?.reservation.marshaCode,
        reservation_id: formData?.reservation.reservationIdentifier,
        guest_first_name: formData?.reservation.customerFirstName,
        guest_last_name: formData?.reservation.customerLastName,
        guest_bonvoy_number: '',
        guest_acid: '',
        check_in_date: formData?.reservation.arrival,
        check_out_date: formData?.reservation.departure,
        timezone_of_guest: '',
        additional_guests_exists:
          formData?.additional_guests_details?.length > 0 || formData?.children_details?.length > 0 ? 'Yes' : 'No',
        additional_guests_details: formData?.additional_guests_details,
        traveling_with_children: formData?.traveling_with_children,
        children_details: formData?.children_details,
        pet_details: formData?.pet_details,
        traveling_with_pets: formData?.traveling_with_pets,
        accessiblity_details: formData?.accessiblity_details,
        accessibility_notes: formData?.accessibility_notes,
        preferred_contact_method_details: formData?.preferred_contact_method_details,
        need_arrival_departure_assistance: formData?.need_arrival_departure_assistance,
        arrival_anticipate_time: formData?.arrival_anticipate_time,
        arrival_method: formData?.arrival_method,
        number_of_passengers_on_arrival: arrivalMethodTransport.number_of_passengers_on_arrival,
        arrival_location: arrivalMethodTransport.arrival_location,
        arrival_location_details: arrivalMethodTransport.arrival_location_details,
        transportation_arrival_date: formData?.transportation_arrival_date,
        transportation_arrival_time: formData?.transportation_arrival_time,
        departure_method: formData?.departure_method,
        number_of_passengers_on_departure: departureMethodTransport.number_of_passengers_on_departure,
        departure_location: departureMethodTransport.departure_location,
        departure_location_details: departureMethodTransport.departure_location_details,
        transportation_departure_time: formData?.transportation_departure_time,
        arrival_dept_notes: formData?.arrival_dept_notes,
        special_occasion_request: formData?.special_occasion_request_category_details?.length > 0 ? 'Yes' : 'No',
        special_occasion_request_category_details: formData?.special_occasion_request_category_details,
        special_occasion_request_other: formData?.special_occasion_request_other,
        special_occasion_magical_request: formData?.special_occasion_request_notes,
        special_occasion_request_notes: formData?.special_occasion_request_other_notes,
        surprise_request: formData?.surprise_request,
        surprise_request_coordination_contact_detail: formData?.surprise_request_coordination_contact_detail,
        additional_requests: formData?.additional_requests,
        dining_plan_details: formData?.dining_plan_details,
        other_dining_details: formData?.other_dining_details,
        dining_dietary_restrictions_notes: formData?.dining_dietary_restrictions_notes,
        experiences_plan_details: formData?.experiences_plan_details,
        dietary_restrictions_notes: formData?.dining_dietary_restrictions_notes,
        dining_notes: formData?.dining_notes,
        other_experience_notes: formData?.dietary_restrictions_notes,
        other_experiences_details: formData?.other_experiences_details,
        dietary_restrictions: formData?.dietary_restrictions,
        dietary_restrictions_details: formData?.dietary_restrictions_details,
        survey_responses: [],
        brand_name: formData?.property_config.brand_name,
        confirmation_html: confirmation_html,
        confirmation_html_optimized: confirmation_html_optimized,
        user_detail_html: user_detail_html,
        emailAddress: formData?.reservation?.cusotmerEmail,
        firstName: formData?.reservation?.customerFirstName,
        lastName: formData?.reservation?.customerLastName,
        confirmationData: {
          attributeName: 'subject_line',
          attributeType: 'String',
          attributeValue: `${formData?.reservation.customerFirstName} ${formData?.reservation.customerLastName} submitted the travel request`,
        },
        consent_checkbox: checkConsentStatus(
          formData?.isUserConsentChecked,
          formData?.isUserEnableSignIn,
          formData?.consentCheckbox
        ),
      };

      // if user do login or not login
      if (!formData?.isSignInFeatureDisable) {
        const response = await fetch(`/api/prearrival/submitForm?marsha_cd=${formData?.marsha_cd}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formDataToSend),
        });

        if (response.ok) {
          const responseData = await response.json(); // Parse response JSON if applicable
          if (responseData.success === false) {
            updateDatalayer('env_api_tile_error_info', `API|400|${props.submitURL}|POST`);
            if (responseData.error === 'Travel Plan Submitted') {
              //Push if the form is submitted
              if (typeof window !== 'undefined') {
                updateDatalayer('form_event', 'form_previously_submitted');
              }
              setFormData({
                ...formData,
                isTPFAlreadySubmitted: true,
                isSubmitDataFlow: false,
              });
              setErrorHeading(responseData.error);
              setErrordesc(responseData.display_error);
              setModalType('alreadySubmittedModal');
            } else {
              setModalType('problemModal');
            }
            setOpenModal(true);
          } else {
            if (formData?.consentCheckbox && !isSignedInUser) {
              setOpenModal(false);
              setFormData({
                ...formData,
                isSignInDrawerOpen: true, // this will open the sign in modal
                isSubmitDataFlow: true, // this will ensure this is a submit flow call
                isReviewPageFlow: true, // this will ensure sign in modal call from review page
                isFormReadyForThankYou: true, // create only for scenerio when user sign in after clicking on submit
                isUserEnableSignIn: false,
              });
            } else {
              setFormData({
                ...formData,
                isReviewPageFlow: true,
              });
              updateDatalayer('form_step_name', 'Thank You');
              handleNextPage('thank-you.mi');
            }
          }
          // Redirect or perform any other actions upon successful submission
        } else {
          setModalType('problemModal');
          console.error('API call failed');
        }
      } else {
        const response = await fetch(`/api/prearrival/submitForm?marsha_cd=${formData?.marsha_cd}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formDataToSend),
        });

        if (response.ok) {
          const responseData = await response.json(); // Parse response JSON if applicable
          if (responseData.success === false) {
            updateDatalayer('env_api_tile_error_info', `API|400|${props.submitURL}|POST`);
            if (responseData.error === 'Travel Plan Submitted') {
              //Push if the form is submitted
              if (typeof window !== 'undefined') {
                updateDatalayer('form_event', 'form_previously_submitted');
              }
              setFormData({
                ...formData,
                isTPFAlreadySubmitted: true,
              });
              setErrorHeading(responseData.error);
              setErrordesc(responseData.display_error);
              setModalType('alreadySubmittedModal');
            } else {
              setModalType('problemModal');
            }
            setOpenModal(true);
          } else {
            setFormData({
              ...formData,
              isSubmitDataFlow: true,
              isTPFAlreadySubmitted: true,
            });
            updateDatalayer('form_step_name', 'Thank You');
            handleNextPage('thank-you.mi');
          }
          // Redirect or perform any other actions upon successful submission
        } else {
          setModalType('problemModal');
          console.error('API call failed');
        }
      }
    } catch (error) {
      setModalType('problemModal');
      console.error('Error submitting form:', error);
    }
  };

  useEffect(() => {
    if (
      formData?.isSubmitDataFlow &&
      !formData?.isReviewPageFlow &&
      !formData?.isUserEnableSignIn &&
      formData?.isTPFAlreadySubmitted
    ) {
      if (!formData?.isThankYouPageLoad) {
        updateDatalayer('form_step_name', 'Thank You');
        handleNextPage('thank-you.mi');
      }
    }
  }, [formData?.isReviewPageFlow]);

  useEffect(() => {
    if (
      formData?.isSubmitDataFlow &&
      formData?.isUserEnableSignIn &&
      formData?.isFormReadyForThankYou &&
      !formData?.isCloseSignInModalOnSubmit
    ) {
      if (!formData?.isThankYouPageLoad) {
        updateDatalayer('form_step_name', 'Thank You');
        handleNextPage('thank-you.mi');
      }
    }
  }, [formData?.isFormReadyForThankYou, formData?.isSubmitDataFlow, formData?.isUserEnableSignIn]);

  useEffect(() => {
    if (formData?.isSubmitDataFlow && !formData?.isUserEnableSignIn && formData?.isCloseSignInModalOnSubmit) {
      if (!formData?.isThankYouPageLoad) {
        updateDatalayer('form_step_name', 'Thank You');
        handleNextPage('thank-you.mi');
      }
    }
  }, [formData?.isCloseSignInModalOnSubmit, formData?.isSubmitDataFlow, formData?.isUserEnableSignIn]);

  const handleSubmitClick = () => {
    setModalType('submitModal');
    setOpenModal(true);
  };
  // Function to get labels based on checkbox names
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getLabels = (names: any) => {
    return (
      names
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .map((name: any) => name)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .filter((label: any) => label !== '')
        .join(', ')
    );
  };

  const sanitizedNode1 = DOMPurify.sanitize(propertyName, { RETURN_DOM: true }) as HTMLElement;
  const fragmentString1 = sanitizedNode1.innerHTML;
  const sanitizedNode2 = DOMPurify.sanitize(labels.subTitle2, { RETURN_DOM: true }) as HTMLElement;
  const fragmentString2 = sanitizedNode2.innerHTML;
  const sanitizedNode3 = DOMPurify.sanitize(labels.subTitle3, { RETURN_DOM: true }) as HTMLElement;
  const fragmentString3 = sanitizedNode3.innerHTML;
  const sanitizedNode4 = DOMPurify.sanitize(labels.subTitle4, { RETURN_DOM: true }) as HTMLElement;
  const fragmentString4 = sanitizedNode4.innerHTML;
  const sanitizedNode5 = DOMPurify.sanitize(labels.subTitle5, { RETURN_DOM: true }) as HTMLElement;
  const fragmentString5 = sanitizedNode5.innerHTML;
  const sanitizedNode6 = DOMPurify.sanitize(description, { RETURN_DOM: true }) as HTMLElement;
  const fragmentString6 = sanitizedNode6.innerHTML;
  const sanitizedNode7 = DOMPurify.sanitize(privacyCenterDescription, { RETURN_DOM: true }) as HTMLElement;
  const fragmentString7 = sanitizedNode7.innerHTML;
  const sanitizedNode8 = DOMPurify.sanitize(consentCheckbox, { RETURN_DOM: true }) as HTMLElement;
  const fragmentString8 = sanitizedNode8.innerHTML;
  const [brandCdSetPage, setBrandCdSetPage] = useState('');
  const [backgroundImageUrl, setBackgroundImageUrl] = useState('');
  useEffect(() => {
    if (props?.brandCode) {
      if (props?.property_config?.non_branded_flg === 'Y') {
        setBrandCdSetPage(brandColorSchemeGenerator('NBF')); // Non-Brand
      } else if (props.brandName === 'Ritz-Carlton Reserve') {
        setBrandCdSetPage(brandColorSchemeGenerator('RZR'));
      } else {
        setBrandCdSetPage(brandColorSchemeGenerator(props?.brandCode));
      }
    } else {
      if (formData?.property_config?.non_branded_flg === 'Y') {
        setBrandCdSetPage(brandColorSchemeGenerator('NBF')); // Non-Brand
      } else {
        setBrandCdSetPage(brandColorSchemeGenerator(formData?.property_config?.brand_cd || formData?.brandCode));
      }
    }
    if (props?.backgroundImage) {
      setBackgroundImageUrl(props?.backgroundImage);
    } else {
      setBackgroundImageUrl(formData?.property_config?.background_image_url);
    }
  }, [
    props?.brandCode,
    formData?.property_config?.brand_cd,
    props?.backgroundImage,
    formData?.property_config?.background_image_url,
  ]);

  const [accordionOpenState, setAccordionOpenState] = useState<{ [key: string]: boolean }>({
    guestDetailSectionTab: false,
    dinninglSectionTab: false,
    experienceSectionTab: false,
  });

  const accordionLoopData = [
    {
      id: 'guestDetailSectionTab',
      title: guestDetailsAccordionLabel,
    },
    {
      id: 'dinninglSectionTab',
      title: diningAccordionLabel,
    },
    {
      id: 'experienceSectionTab',
      title: experienceAccordionLabel,
    },
  ];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getAccordionOptionsContent = (id: string): any => {
    if (id === 'guestDetailSectionTab') {
      return (
        <div className="col-md flex-row d-flex d-flex-row dropdown-box">
          <div className="col-md-6 pl-0 mb-0">
            <div>
              <b>{firstLastName}</b>
            </div>
            <div>
              <b>{(props?.i18nMap?.reservationNumber ?? 'Reservation #') + ':'}</b> {reservation_num}
            </div>
            <div>
              <b>{(props?.i18nMap?.arrival ?? 'Arrival') + ':'}</b> {arrivalDate}
            </div>
            <div>
              <b>{(props?.i18nMap?.departure ?? 'Departure') + ':'}</b> {departureDate}
            </div>
            <div>
              <b>{(props?.i18nMap?.travelCompanionInfo ?? 'Travel Companion Special Information') + ':'} </b>

              {formData?.additional_guests_details.length <= 0 &&
                formData?.pet_details.length <= 0 &&
                formData?.children_details.length <= 0 && <span>None</span>}
              {formData?.additional_guests_details.length > 0 && (
                <div>
                  <b>{(props?.i18nMap?.guest ?? 'Guest') + ':'}</b>

                  <ol>
                    {
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      formData?.additional_guests_details.map((guest: any, index: any) => (
                        <li key={index}>
                          {guest.title && `${guest.title}`}
                          {guest.title && guest.guest_first_name
                            ? ` ${guest.guest_first_name}`
                            : guest.guest_first_name}
                          {(guest.guest_first_name || guest.title) && guest.guest_last_name
                            ? ` ${guest.guest_last_name}`
                            : guest.guest_last_name}
                          {(guest.guest_last_name || guest.guest_first_name || guest.title) && guest.relationship_type
                            ? `, ${guest.relationship_type}`
                            : guest.relationship_type}{' '}
                        </li>
                      ))
                    }
                  </ol>
                </div>
              )}
              {formData?.children_details.length > 0 && (
                <div>
                  <b>Child: </b>
                  <ol>
                    {
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      formData?.children_details.map((child: any, index: any) => (
                        <li key={index}>
                          {(child.childFirstName || child.childLastName) && (
                            <>
                              {child.childFirstName}
                              {child.childFirstName && child.childLastName ? ' ' : ''}
                              {child.childLastName}
                            </>
                          )}

                          {child.childAge
                            ? `${child.childFirstName || child.childLastName ? ', ' : ''}${
                                child.childAge == '<1' || child.childAge == '1' || child.childAge < 2
                                  ? `${child.childAge} Year`
                                  : `${child.childAge} Years`
                              }`
                            : ''}

                          {(child.childFirstName || child.childLastName || child.childAge) && child.childNotesArea ? (
                            <>
                              <br />

                              <b>{(props?.i18nMap?.notes ?? 'Notes') + ': '}</b>

                              {child.childNotesArea}
                            </>
                          ) : (
                            child.childNotesArea && (
                              <>
                                <b>{(props?.i18nMap?.notes ?? 'Notes') + ': '}</b>

                                {child.childNotesArea}
                              </>
                            )
                          )}
                        </li>
                      ))
                    }
                  </ol>
                </div>
              )}
              {formData?.pet_details.length > 0 && (
                <div>
                  <b>{(props?.i18nMap?.pet ?? 'Pet') + ':'} </b>

                  <ol>
                    {
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      formData?.pet_details.map((pet: any, index: any) => (
                        <li key={index}>
                          {pet.petName || pet.petBreed ? (
                            <>
                              {pet.petName}
                              {pet.petName && pet.petBreed && `, ${pet.petBreed}`}
                              {pet.petBreed && !pet.petName && pet.petBreed}{' '}
                              {pet.petNotesArea && (
                                <>
                                  <br />

                                  <b>{(props?.i18nMap?.notes ?? 'Notes') + ':'} </b>

                                  {pet.petNotesArea}
                                </>
                              )}
                            </>
                          ) : (
                            pet.petNotesArea && (
                              <>
                                <b>{(props?.i18nMap?.notes ?? 'Notes') + ':'} </b>

                                {pet.petNotesArea}
                              </>
                            )
                          )}
                        </li>
                      ))
                    }
                  </ol>
                </div>
              )}
            </div>
            <div>
              <b>{(props?.i18nMap?.arrivalMethod ?? 'Arrival Method') + ': '} </b>
              {arrivalMethod ? arrivalMethod : 'None'}
            </div>
            <div>
              <b>{(props?.i18nMap?.arrivalDate ?? 'Arrival Date') + ': '} </b>
              {arrivalPropertyTime ? arrivalPropertyTime : 'None'}
            </div>
            <div>
              <b>{(props?.i18nMap?.arrivalTiming ?? 'Arrival Timing') + ': '} </b>
              {arrivalTiming ? arrivalTiming : 'None'}
            </div>

            <div>
              <b>Arrival Location: </b>
              {formData.arrivalInfo ? formData.arrivalInfo : 'None'}
            </div>
            <div>
              <b>Arrival Location Details: </b>
              {formData.arrivalFlightNumber ? formData.arrivalFlightNumber : 'None'}
            </div>
            <div>
              <b>Number of Passengers on Arrival: </b>
              {formData.arrivalPassengers ? formData.arrivalPassengers : 'None'}
            </div>

            <div>
              <b>{(props?.i18nMap?.departureMethod ?? 'Departure Method') + ': '} </b>
              {departureMethod ? departureMethod : 'None'}
            </div>
            <div>
              <b>{(props?.i18nMap?.departureDate ?? 'Departure Date') + ': '} </b>

              {departurePropertyTime ? departurePropertyTime : 'None'}
            </div>

            <div className="mb-0 deptText">
              <b>{(props?.i18nMap?.departureTiming ?? 'Departure Timing') + ': '} </b>

              {departureTiming ? departureTiming : 'None'}
            </div>
          </div>

          <div className="col-md-6 pl-0 mb-0">
            <div>
              <b>Departure Location: </b>
              {formData.departureInfo ? formData.departureInfo : 'None'}
            </div>
            <div>
              <b>Departure Location Details: </b>
              {formData.departureFlightNumber ? formData.departureFlightNumber : 'None'}
            </div>
            <div>
              <b>Number of Passengers on Departure: </b>
              {formData.departurePassengers ? formData.departurePassengers : 'None'}
            </div>
            <div>
              <b>{(props?.i18nMap?.assistNotes ?? 'Assist Notes') + ': '} </b>
              {removeXSSScripts(arrivalDeptNotes) ? removeXSSScripts(arrivalDeptNotes) : 'None'}
            </div>
            <div>
              <b>{(props?.i18nMap?.arrivalAnticipateTime ?? 'Arrival Anticipate Time') + ': '} </b>
              {arrivalAnticipateTime ? arrivalAnticipateTime : 'None'}
            </div>
            <div>
              <b>{(props?.i18nMap?.accessibilityNeeds ?? 'Accessibility/Disability Needs') + ': '} </b>

              {accessibilityNeeds === '' || accessibilityNeeds === 'No'
                ? 'No'
                : getLabels(formData?.accessiblity_details)}
            </div>
            <div>
              <b>{(props?.i18nMap?.accessibilityNote ?? 'Accessibility Note') + ': '} </b>
              {removeXSSScripts(accessibilityNotes) ? removeXSSScripts(accessibilityNotes) : 'None'}
            </div>
            <div className="trip-contact-preference">
              <b>{(props?.i18nMap?.tripContactPreference ?? 'Trip Contact Preference') + ': '} </b>

              {[
                { type: 'email', checkbox: tripEmailCheckbox },
                { type: 'phone', checkbox: tripPhoneCheckbox },
                { type: 'text', checkbox: tripTextCheckbox },
                { type: 'wechat', checkbox: tripWechatCheckbox },
                { type: 'whatsapp', checkbox: tripWhatsappCheckbox },
              ]
                .filter(({ checkbox }) => checkbox)
                .map(({ type }) => {
                  const detail =
                    preferredContactMethodDetails.find((item: any) => item.contact_method_type === type)
                      ?.contact_method_detail || '';

                  const timezone =
                    preferredContactMethodDetails.find((item: any) => item.contact_method_type === type)
                      ?.timezone_of_guest || '';

                  if (!detail) return null;

                  return (
                    <span key={type}>
                      {detail}
                      {type !== 'email' && timezone ? ` ${timezone}` : ''}
                    </span>
                  );
                })
                .filter(Boolean)
                .map((el, i, arr) => (
                  <span key={i}>
                    {el}
                    {i < arr.length - 1 && ', '}
                  </span>
                ))}

              {!tripEmailCheckbox &&
              !tripPhoneCheckbox &&
              !tripTextCheckbox &&
              !tripWechatCheckbox &&
              !tripWhatsappCheckbox ? (
                <span>None</span>
              ) : (
                tripPhoneCheckbox && ''
              )}
            </div>

            <div>
              <b>{(props?.i18nMap?.specialOccasion ?? 'Special Occasion') + ': '} </b>

              {specialOccasionRequestCategoryDetails.length > 0
                ? getLabels(specialOccasionRequestCategoryDetails)
                : 'No'}
            </div>
            <div>
              <b>{(props?.i18nMap?.specialOccasionNotes ?? 'Special Occasion Notes') + ': '} </b>

              {specialOccasionRequestOtherNotes ? specialOccasionRequestOtherNotes : 'None'}
            </div>

            <div>
              <b>{(props?.i18nMap?.magicalMomentNotes ?? 'Magical Moment Notes') + ': '} </b>
              {removeXSSScripts(specialOccasionRequestNotes) ? removeXSSScripts(specialOccasionRequestNotes) : 'None'}
            </div>
            <div>
              <b>{(props?.i18nMap?.occasionSurprise ?? 'Is this Occasion a surprise') + ': '} </b>
              {surpriseRequest ? surpriseRequest : 'No'}
            </div>
            <div className="trip-contact-preference">
              <b>{(props?.i18nMap?.surpriseContactPreference ?? 'Surprise Contact Preference') + ': '} </b>

              {[
                { type: 'email', checked: soTripCustomerEmail },
                { type: 'phone', checked: sotripPhoneNumber },
                { type: 'text', checked: sotripTextNumber },
                { type: 'wechat', checked: sotripWeChatNumber },
                { type: 'whatsapp', checked: sotripWhatsAppNumber },
              ]
                .filter(({ checked }) => checked) // Only include selected contact methods
                .map(({ type }) => {
                  const detail =
                    surpriseRequestCoordinationContactDetail.find(
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      (item: any) => item.contact_method_type === type
                    )?.contact_method_detail ||
                    (type === 'email' && customerEmail) ||
                    '';

                  return detail && <span key={type}>{detail}</span>;
                })
                .reduce(
                  (prev, curr, index) => (
                    <>
                      {prev}
                      {index > 0 && ', '}
                      {curr}
                    </>
                  ),
                  null
                )}

              {!soTripCustomerEmail &&
                !sotripPhoneNumber &&
                !sotripTextNumber &&
                !sotripWeChatNumber &&
                !sotripWhatsAppNumber && <span>None</span>}
            </div>

            <div className="mb-0">
              <b>{(props?.i18nMap?.additionalInfo ?? 'Any Additional Information') + ': '} </b>

              {additionalRequest ? removeXSSScripts(additionalRequest) : 'None'}
            </div>
          </div>
          <Edit page="guest-details" />
        </div>
      );
    }
    if (id === 'dinninglSectionTab') {
      return (
        <div className="col-md flex-row dropdown-box">
          <div className="mb-3">
            <b>{(props?.i18nMap?.diningAdded || 'Dining Added to Your Plan') + ': '} </b>
            {!hasDining
              ? 'None'
              : // eslint-disable-next-line @typescript-eslint/no-explicit-any
                formData?.dining_plan_details
                  .filter((detail: DiningPlanDetail) => detail.reservation_req === '0')
                  .map((detail: DiningPlanDetail, index: number) => (
                    <span key={index}>
                      {detail.dining_location}
                      {index <
                        formData?.dining_plan_details.filter(
                          (detail: DiningPlanDetail) => detail.reservation_req === '0'
                        ).length -
                          1 && ', '}
                    </span>
                  ))}
          </div>
          <div className="mb-3">
            <b>{(props?.i18nMap?.diningReservationsAdded ?? 'Dining Reservations Added to Your Plan') + ': '} </b>

            {!hasData && 'None'}
          </div>
          {hasData &&
            Object.keys(groupedDiningDetails).map(date => (
              <div className="pl-3" key={date}>
                <div>{date && <b>{date}</b>}</div>
                {groupedDiningDetails[date].map((detail, index) => (
                  <div key={index}>
                    {detail.type === 'Preferred' && detail.prefered_date && (
                      <div>
                        {detail.dining_location} {detail.prefered_time}, Party for {detail.party_size} ({detail.type})
                      </div>
                    )}
                    {detail.type === 'Alternative' && detail.alternate_date && (
                      <div>
                        {detail.dining_location} {detail.alternate_time}, Party for {detail.party_size} ({detail.type})
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}
          <div className="mt-3">
            {' '}
            <b>{(props?.i18nMap?.otherDiningExperiences ?? 'Other Dining Experiences') + ': '} </b>
            {formData?.other_dining_details.length > 0 ? (
              <span>{getLabels(formData?.other_dining_details)}</span>
            ) : (
              'No'
            )}
          </div>
          <div className="mt-3">
            {' '}
            <b>{(props?.i18nMap?.diningNotes ?? 'Dining Experiences Notes') + ': '} </b>
            {formData?.dining_notes ? formData?.dining_notes : 'None'}
          </div>
          <div className="mt-3">
            {' '}
            <b>{(props?.i18nMap?.dietaryPreferences ?? 'Dietary Preferences') + ': '} </b>
            {formData?.dietary_restrictions_details.length > 0 ? (
              <span>{getLabels(formData?.dietary_restrictions_details)}</span>
            ) : (
              'No'
            )}
          </div>
          <div className="mt-3">
            {' '}
            <b>{(props?.i18nMap?.dietaryPreferencesNotes ?? 'Dietary Preferences Notes') + ': '} </b>
            {formData?.dining_dietary_restrictions_notes ? formData?.dining_dietary_restrictions_notes : 'None'}
          </div>

          <Edit page="dining" />
        </div>
      );
    }
    if (id === 'experienceSectionTab') {
      return (
        <div className="col-md flex-row dropdown-box">
          <div className="mb-3">
            <b>{(props?.i18nMap?.activitiesAdded ?? 'Activities & Experiences Added to Your Plan') + ': '} </b>{' '}
            {!hasActivity
              ? 'None'
              : formData?.experiences_plan_details
                  .filter((detail: DiningPlanDetail) => detail.reservation_req === '0')
                  .map((detail: DiningPlanDetail, index: number) => (
                    <span key={index}>
                      {detail.dining_location}
                      {index <
                        formData?.experiences_plan_details.filter(
                          (detail: DiningPlanDetail) => detail.reservation_req === '0'
                        ).length -
                          1 && ', '}
                    </span>
                  ))}
          </div>
          <div className="mb-3">
            <b>
              {(props?.i18nMap?.activitiesReservationAdded ??
                'Activities & Experiences Reservation Added to Your Plan') + ': '}
            </b>
            {!hasActivityData && 'None'}
          </div>
          {hasActivityData &&
            Object.keys(groupedActivitiesDetails).map(date => (
              <div className="pl-3" key={date}>
                <div>{date && <b>{date}</b>}</div>
                {groupedActivitiesDetails[date].map((detail, index) => (
                  <div key={index}>
                    {detail.type === 'Preferred' && detail.prefered_date && (
                      <div>
                        {detail.dining_location} {detail.prefered_time}, Party for {detail.party_size} ({detail.type})
                      </div>
                    )}
                    {detail.type === 'Alternative' && detail.alternate_date && (
                      <div>
                        {detail.dining_location} {detail.alternate_time}, Party for {detail.party_size} ({detail.type})
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}

          <div className="mt-3">
            <b>{(props?.i18nMap?.additionalOpportunities ?? 'Additional Opportunities') + ': '} </b>

            {formData?.other_experiences_details.length > 0 ? (
              <span>{getLabels(formData?.other_experiences_details)}</span>
            ) : (
              'None'
            )}
          </div>
          <div className="mt-3">
            <b>{(props?.i18nMap?.additionalOpportunitiesNotes ?? 'Additional Opportunities Notes') + ': '} </b>

            {formData?.dietary_restrictions_notes ? formData?.dietary_restrictions_notes : 'None'}
          </div>

          <Edit page="experiences" />
        </div>
      );
    }
  };

  if (stayValidationError) {
    return (
      <SurveyWrapper
        marshaCode={marshaCode}
        backgroundImage={backgroundImageUrl}
        className="brand-css common-color-set"
        colorScheme={brandCdSetPage}
        alternateId={alternateCID}
        productInfo={marshaCode}
        formPageName="Review"
        formName="Prearrival Travel Form"
        env_api_tile_error_info=""
        prearrival_form_amenities_added_to_plan={formData?.prearrivalformamenitiesaddedtoplan}
        prearrival_form_booking_confirmation_id={combinedValue}
      >
        <ErrorDetail
          hotelEmail={props?.hotelEmail}
          hotelPhoneNumber={props?.hotelPhoneNumber}
          confirmationNumber={formData?.reservation?.confirmationNumber}
          errorVal={stayErrorMsg}
        />
      </SurveyWrapper>
    );
  }

  return (
    <>
      {isLoading ? (
        <SurveyWrapper
          marshaCode={marshaCode}
          backgroundImage={backgroundImageUrl}
          className="brand-css common-color-set"
          colorScheme={brandCdSetPage}
          alternateId={alternateCID}
          productInfo={marshaCode}
          formPageName="Review"
          formName="Prearrival Travel Form"
          env_api_tile_error_info=""
          prearrival_form_amenities_added_to_plan={formData?.prearrivalformamenitiesaddedtoplan}
          prearrival_form_booking_confirmation_id={combinedValue}
          form_prearrival_questions_complete={form_prearrival_questions_complete}
        >
          <StyledSubmitContainer data-testid="submit-container" data-component-name="o-account-submitContainer">
            <div className="container">
              <div className="row">
                <div className="col-12 pr-0 px-0">
                  <div className="lr-no-padding container col-12 col-xl-10 submitContainer">
                    <div className="d-flex flex-column">
                      {/* Heading */}
                      <div className="skeleton-loader" style={{ height: '32px', width: '60%', marginBottom: '16px' }} />
                      {/* Hotel Name */}
                      <div
                        className="skeleton-loader hotel-name"
                        style={{ height: '20px', width: '40%', marginBottom: '16px' }}
                      />

                      {/* Names Block */}
                      <div className="d-flex justify-content-start mb-3">
                        <div className="d-item-container">
                          <div className="d-item">
                            <div
                              className="skeleton-loader"
                              style={{ height: '18px', width: '120px', marginBottom: '8px' }}
                            />
                          </div>
                          <div className="d-item">
                            <div className="skeleton-loader" style={{ height: '18px', width: '100px' }} />
                          </div>
                        </div>
                        <div className="d-item-container ml-3">
                          <div className="d-item">
                            <div
                              className="skeleton-loader"
                              style={{ height: '18px', width: '80px', marginBottom: '8px' }}
                            />
                          </div>
                          <div className="d-item">
                            <div className="skeleton-loader" style={{ height: '18px', width: '120px' }} />
                          </div>
                        </div>
                      </div>
                      {/* Subtext */}
                      <div
                        className="skeleton-loader subtext mb-2"
                        style={{ height: '18px', width: '70%', marginBottom: '24px' }}
                      />

                      {/* Accordions */}
                      {accordionLoopData.map((_, index) => (
                        <div
                          key={index}
                          className="skeleton-loader"
                          style={{ height: '40px', width: '100%', marginBottom: '16px' }}
                        />
                      ))}

                      {/* Privacy text */}
                      <div
                        className="skeleton-loader privacytext mb-2"
                        style={{ height: '16px', width: '60%', marginTop: '24px', marginBottom: '24px' }}
                      />

                      {/* Buttons */}
                      <div className="align-items-end justify-content-end d-flex d-flex-mobile col-12 p-0 btn-wrappper">
                        <div
                          className="skeleton-loader"
                          style={{ height: '40px', width: '120px', marginRight: '12px' }}
                        />
                        <div className="skeleton-loader" style={{ height: '40px', width: '120px' }} />
                      </div>

                      {/* Optional modal placeholder */}
                      {openModal && (
                        <div
                          className="skeleton-loader"
                          style={{ height: '200px', width: '100%', marginTop: '32px' }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </StyledSubmitContainer>
        </SurveyWrapper>
      ) : (
        <SurveyWrapper
          marshaCode={marshaCode}
          backgroundImage={backgroundImageUrl}
          className="brand-css common-color-set"
          colorScheme={brandCdSetPage}
          alternateId={alternateCID}
          productInfo={marshaCode}
          formPageName="Review"
          formName="Prearrival Travel Form"
          env_api_tile_error_info=""
          prearrival_form_amenities_added_to_plan={formData?.prearrivalformamenitiesaddedtoplan}
          prearrival_form_booking_confirmation_id={combinedValue}
          form_prearrival_questions_complete={form_prearrival_questions_complete}
        >
          <StyledSubmitContainer data-testid="submit-container" data-component-name="o-account-submitContainer">
            <div className="container">
              <div className="row">
                <div className="col-12 pr-0 px-0">
                  <div className="lr-no-padding container col-12 col-xl-10 submitContainer">
                    <div className="d-flex flex-column">
                      <Heading
                        variation={Types.headingType.title}
                        fontSize={Types.size.large}
                        element={Types.tags.h1}
                        titleText={title}
                        disableCustomClickTrack={true}
                      />
                      <Text
                        element={Types.tags.paragraph}
                        fontSize={Types.size.medium}
                        dangerouslySetInnerHTML={{ __html: fragmentString1 }}
                        customClass="hotel-name"
                        data-testid="hotel address"
                      ></Text>

                      <div className="d-flex justify-content-start mb-3">
                        <div className="d-item-container">
                          <div className="d-item">
                            <Text
                              element={Types.tags.paragraph}
                              fontSize={Types.size.medium}
                              dangerouslySetInnerHTML={{ __html: fragmentString2 }}
                              customClass="mb-2 bold"
                              data-testid="First Name"
                            ></Text>
                          </div>
                          <div className="d-item">
                            <Text
                              element={Types.tags.paragraph}
                              fontSize={Types.size.medium}
                              dangerouslySetInnerHTML={{ __html: fragmentString4 }}
                              data-testid="Second Name"
                            ></Text>
                          </div>
                        </div>
                        <div className="d-item-container ml-3">
                          <div className="d-item">
                            <Text
                              element={Types.tags.paragraph}
                              fontSize={Types.size.medium}
                              dangerouslySetInnerHTML={{ __html: fragmentString3 }}
                              customClass="mb-2"
                            ></Text>
                          </div>
                          <div className="d-item">
                            <Text
                              element={Types.tags.paragraph}
                              fontSize={Types.size.medium}
                              dangerouslySetInnerHTML={{ __html: fragmentString5 }}
                            ></Text>
                          </div>
                        </div>
                      </div>
                      <Text
                        element={Types.tags.div}
                        fontSize={Types.size.medium}
                        dangerouslySetInnerHTML={{ __html: fragmentString6 }}
                        customClass="subtext mb-2"
                      ></Text>
                      {accordionLoopData.map((option, index) => (
                        <div key={index}>
                          <Accordion
                            id={option?.id}
                            key={index + option.id}
                            enableAccordionSeparator={true}
                            disableAccResize={true}
                            isOpen={accordionOpenState[option?.id]}
                            headerChildren={option?.title}
                            customClass="review-accordion"
                            handleHeaderClick={(id: string) =>
                              setAccordionOpenState(prevState => ({
                                ...prevState,
                                [id]: !prevState[id],
                              }))
                            }
                          >
                            {getAccordionOptionsContent(option.id)}
                          </Accordion>
                        </div>
                      ))}
                      {enableConsentCheckboxFeature === true && (
                        <ConsentCheckbox
                          checked={formData?.consentCheckbox}
                          fragmentString={fragmentString8}
                          marshaCode={marshaCode}
                          requestId={props?.requestId}
                          sessionData1={props?.sessionData1}
                          operationSignatures={props?.operationSignatures}
                          privacyText={fragmentString7}
                        />
                      )}
                      <div className="align-items-end justify-content-end d-flex d-flex-mobile col-12 p-0 btn-wrappper">
                        <Button
                          className="m-button-m m-button-secondary custom-btn mr-3 "
                          type={Types.ButtonTypeVariation.Button}
                          buttonCopy="Previous"
                          callback={() => handleNextPage('experiences.mi')}
                        ></Button>
                        <Button
                          className="m-button-m m-button-primary custom-btn mx-0 "
                          type={Types.ButtonTypeVariation.Button}
                          buttonCopy={props?.i18nMap?.submit ?? 'Submit'}
                          callback={handleSubmitClick}
                        ></Button>
                      </div>

                      {openModal && (
                        <GenericDialogueModal
                          isVisible={openModal}
                          modalType={modalType}
                          errorHeading={errorHeading}
                          errordesc={errordesc}
                          onClose={handleClose}
                          onSubmit={handleSubmitButton}
                        />
                      )}
                      {/* {formData?.isSignInDrawerOpen &&
                        !formData?.isSignInFeatureDisable &&
                        !formData?.isUserEnableSignIn && (
                          <SignInModal
                            show={formData?.isSignInDrawerOpen}
                            onClose={handleSignInClose}
                            modalData={props?.model}
                          />
                        )} */}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </StyledSubmitContainer>
        </SurveyWrapper>
      )}
    </>
  );
};

export const SubmitFormWrapperConfig = {
  emptyLabel: 'Submit',
  isEmpty: false,
  resourceType: `mi-aem-martech/components/submit`,
};

/* eslint-disable  @typescript-eslint/no-explicit-any */
export const Submit = (props: any) => {
  return (
    <EditableComponent {...props} config={SubmitFormWrapperConfig}>
      <SubmitForm {...props} />
    </EditableComponent>
  );
};
